import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { sumDecimalStrings } from '@/lib/decimal';
import {
  defaultQuantityFor,
  quantityStepError,
  type ProductUnitFields,
} from '@/lib/product-units';
import { CURRENCY_PATTERN } from '@/lib/document-validation';
import { futureBillablesDenialReason } from '@/lib/permissions';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  projectInCycleScope,
  projectOptionsByScope,
  projectScopeFootnote,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { KIND_LABELS } from '@/lib/tracker-kind';
import { useAuth } from '@/lib/use-auth';
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
import { useCycleScopeIds } from '@/lib/use-line-project';
import { isRealDate } from '@/components/catalog/form-utils';
import { Notice } from '@/components/ui/Notice';
import {
  Button,
  Combobox,
  Field,
  Input,
  Modal,
  SegmentedControl,
  Select,
  Textarea,
  type ComboboxOption,
  type SegmentedControlOption,
} from '@/components/ui';
import type {
  InvoiceTrackerKind,
  InvoiceTrackerPlannedProductInput,
  InvoiceTrackerRow,
  InvoiceTrackerStandaloneInput,
  InvoiceTrackerUpdateInput,
  Project,
} from '@/lib/types';

/*
 * PLANNING A FUTURE QUOTE OR INVOICE — the Accounts Dashboard's third section.
 *
 * ⚠️ **IT WRITES A TRACKER ROW AND A REMINDER TASK. IT RAISES NOTHING.** No
 * quote is created, no invoice is created, nothing reaches Xero and no money is
 * asked for. Raising the document is done the ordinary way through the quote or
 * invoice screens, and doing so is what removes the row from both this dashboard
 * and the invoice tracker's Pending view. The title and the submit label say
 * "plan" and "planned" for that reason: a word like "Create invoice" here would
 * read as a demand for money somebody had merely made a note about.
 *
 * ⚠️ *SUPERSEDED: "The title, the ON-SCREEN SENTENCE and the submit label".*
 * There is no on-screen sentence any more — the modal's `description` and every
 * `hint` were removed on the owner's instruction, 2026-09-03. **So the wording
 * of the TITLE and the SUBMIT LABEL is now the only thing on screen carrying
 * this distinction**, which makes them load-bearing rather than merely
 * consistent: renaming either to a verb that sounds like raising would leave
 * nothing to correct it.
 *
 * ## It is the SAME ROW as the invoice tracker's Pending view
 *
 * Not a lookalike — one table behind two screens (`POST /api/v1/invoice-tracker`,
 * the standalone create). A row added here appears there, and vice versa. This
 * dialog is deliberately a NARROWER form over the same endpoint than
 * `invoice-tracker/StandaloneRowDialog`, which it is otherwise modelled on
 * closely: the owner named four fields for this screen, and a second full-width
 * form over one endpoint is two places to keep one contract right.
 *
 * ⚠️ **NEVER the way to record dates against a document that already exists.**
 * That is the upsert (`PUT /invoice-tracker`, which the tracker's own inline
 * fields use). A standalone row for a real document puts the same money on the
 * tracker twice, and nothing anywhere reconciles the two.
 *
 * ## The party is one of two things, never both
 *
 * A row names an existing company OR a typed name. The server refuses a row
 * carrying neither, as a 422 on `partyName` — mirrored below so somebody is told
 * before the round trip, and still routed if it arrives anyway. Sending both is
 * unexpressible: the mode decides which key is built.
 *
 * ## Contract
 *
 * ```
 * POST /api/v1/invoice-tracker  -> { data: InvoiceTrackerRow }
 *      body InvoiceTrackerStandaloneInput, `.strict()`
 * ```
 */

/**
 * The keys this form can bind a 422 to.
 *
 * Routed against the form's OWN list rather than against the shape of the path:
 * a message keyed on something no input is bound to renders nowhere while still
 * making the error map non-empty, and the usual "fields are highlighted below"
 * summary then highlights nothing.
 */
const FIELD_KEYS = [
  'kind',
  'companyId',
  /*
   * ⚠️ **REQUIRED HERE THE DAY THE SERVER STARTS REQUIRING IT, AND ABSENT FROM
   * THIS LIST IS HOW THAT GOES WRONG.** The owner's decision (2026-09-17) is
   * that `POST /invoice-tracker` will refuse a plan naming no project. A 422 on
   * a key this list does not carry renders NOWHERE while still making the error
   * map non-empty — so the banner says "one field needs attention, highlighted
   * below" over a form with nothing highlighted, which is the failure this
   * codebase records repeatedly. It is routed from the moment the picker exists
   * rather than when the refusal starts arriving.
   */
  'projectId',
  'partyName',
  'currency',
  'amount',
  'plannedRaiseFrom',
  'comment',
  /*
   * ⚠️ The BASKET, not one row of it. A refusal naming `products` is about the
   * set — too many, or the key on a row that may not carry one — and renders
   * above the list.
   *
   * A per-entry refusal arrives at an INDEXED path (`products.0.quantity`), and
   * that is deliberately absent from this list: routing it would need the index
   * parsed back out of the path and matched to a draft row, which is matching on
   * wording by another name — get it wrong and the message marks a DIFFERENT
   * product's row, which is worse than not marking one. Those land in the
   * form-level banner, quoting the path the server sent, which names the row a
   * person can count to.
   */
  'products',
] as const;

type FieldKey = (typeof FIELD_KEYS)[number];

/**
 * ⚠️ READ FROM THE SERVER SCHEMA, NOT ESTIMATED.
 * `src/modules/invoice-tracker/invoice-tracker.schema.ts`, measured 2026-09-02:
 * `partyName: optionalText(200)`, `comment: optionalText(2_000)`.
 *
 * Both directions of a guessed limit fail, and they fail differently — a limit
 * set TOO LOOSE lets somebody type a value the server then refuses, while one
 * set TOO STRICT silently stops accepting characters the server would have
 * taken. Nothing errors on the second; the text just stops. That is enforcing
 * client-side something the server permits, which is the worse of the two.
 */
const PARTY_NAME_MAX = 200;
const REF_NOTES_MAX = 2_000;

/**
 * The server's own money shape, for a PLANNED LINE amount — read from
 * `invoice-tracker.schema.ts`'s `MONEY_PATTERN`, 2026-09-03. `DECIMAL(14,2)`
 * SIGNED: twelve digits ahead of the point, at most two behind, an optional
 * leading minus.
 *
 * ⚠️ **A MINUS IS LEGAL AND MUST STAY LEGAL.** A credit note is expected
 * billing; neither `d_invoice_tracker_rows.amount` nor
 * `f_invoice_tracker_row_products.amount` (migration 147) carries a sign
 * constraint, so a `>= 0` guard here would refuse a real plan.
 *
 * ⚠️ **This MIRRORS the server exactly, where the ROW amount above it is
 * deliberately looser — and the difference is WHERE each refusal lands, not a
 * disagreement about the rule.** A refusal about the row amount arrives at
 * `amount`, which {@link FIELD_KEYS} routes, so the box is marked and the
 * server's own sentence is the better one. A refusal about a LINE arrives at
 * `products.0.amount`, which this form deliberately does not route — it renders
 * in the form-level banner and the row itself is left unmarked. Exactly the
 * argument the planned QUANTITY check already makes a few hundred lines below;
 * the two are checked here for one reason.
 */
const PLANNED_LINE_AMOUNT_PATTERN = /^-?\d{1,12}(\.\d{1,2})?$/;

/** One page of company options; the endpoint searches server-side beyond it. */
const OPTION_PER_PAGE = 100;

/**
 * The one class every group heading on this form uses.
 *
 * A constant rather than the string repeated three times: the headings are the
 * only thing giving this dialog its shape, and one of them drifting a weight or
 * a colour is the sort of difference nobody reports and everybody feels.
 */
const GROUP_LABEL = 'm-0 text-xs font-medium text-fg-secondary';

/**
 * The required marker for a group heading, copied from `Field`'s own.
 *
 * ⚠️ **It has to live here because `Field`'s marker is INSIDE the label, and
 * this form has a required control that hides its own.** The party picker is
 * labelled by the "Who it is for" heading above it rather than by a second label
 * of its own — that duplication was part of what made this dialog feel dense —
 * so passing `required` to that Field would render the asterisk into an
 * `sr-only` element and mark nothing on screen. Marking the heading is what
 * keeps the asterisk where a person can see it.
 *
 * ⚠️ *SUPERSEDED 2026-09-03: "**two** of this form's required controls hide
 * theirs. The party picker **and the kind toggle** are labelled by the group
 * heading above them."* The kind control is an ordinary `Field` + `Select` now
 * (owner's instruction) and takes `required` itself, so it marks its own label
 * and does not come through here. **One caller left** — which is worth stating
 * rather than deleting the helper for: it is still the only way to mark the
 * party group, and a second group would need it again.
 */
function RequiredMark() {
  return (
    <span className="ml-0.5 text-danger" aria-hidden="true">
      *
    </span>
  );
}

/**
 * Which half of the party control is in use.
 *
 * A MODE rather than an inline "add a company": a company is an account with an
 * owner, a stage, a billing country and Xero defaults, and inventing one from a
 * name typed while planning a document is how a directory fills with
 * half-records. Recording that a document is coming does not make the other
 * party a CRM account.
 */
type PartyMode = 'company' | 'name';

/**
 * Companies for the picker, searched server-side.
 *
 * ⚠️ A near-copy of the one in `invoice-tracker/StandaloneRowDialog`, and
 * deliberately not an import from it: exporting a non-component from a component
 * file is the one thing in `src/` that breaks fast refresh. Thirty lines of
 * duplication against every screen in this repo losing its refresh behaviour.
 */
function useCompanyOptions() {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(false);
  /*
   * The term the loaded options answer — null until the first read settles.
   * Deriving `loading` from it rather than holding a flag means a new term reads
   * as loading during the very render that set it, with no setState inside the
   * effect to start a second render pass.
   */
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);
  const loading = loadedTerm !== term;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listCompanies(
        {
          page: 1,
          perPage: OPTION_PER_PAGE,
          sort: 'name',
          order: 'asc',
          ...(term ? { search: term } : {}),
        },
        signal,
      )
      .then((result) => {
        setOptions(
          result.data.map((company) => ({ value: String(company.id), label: company.name })),
        );
        setTotal(result.meta.total);
        setFailed(false);
      })
      .catch(() => {
        /*
         * ⚠️ `failed`, not merely empty. Both render no options, and telling
         * somebody there are no companies when the request simply did not come
         * back sends them to type a name for a party already on file.
         */
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
  }, [term]);

  return { options, total, loading, failed, setTerm };
}

/**
 * The catalogue for the basket picker, searched server-side.
 *
 * ⚠️ **`active: true`, matching every other product picker in this app**
 * (`LineItemFormModal`, `useProjectEditOptions`, `InlineTravelProduct`). A
 * retired product is one nobody may sell now, and this basket is a plan for
 * work not yet raised — so offering one plans a line the quote form will not
 * take.
 *
 * ⚠️ A near-copy of `useCompanyOptions` above rather than a shared export, for
 * the reason recorded there: a non-component export from a component file is
 * the one thing in `src/` that breaks fast refresh.
 *
 * ⚠️ **IT KEEPS EACH PRODUCT'S `unitPrice`, WHICH THE FIRST CUT THREW AWAY.**
 * The catalogue figure is what seeds a planned line's amount, and it arrives on
 * the very response the options are built from — so keeping it costs nothing,
 * where a second read would be a second question with its own failure mode.
 */
function useProductOptions() {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  /**
   * Product id (as the option's string value) → its catalogue `unitPrice`.
   *
   * ⚠️ **`has()` IS THE QUESTION, NEVER `get() ?? null`, AND THAT IS THE WHOLE
   * REASON THIS IS A `Map`.** Three states, and two of them are `null`-shaped:
   *
   *  - **listed with a price** — `"18000.00"`, or `"0.00"`, a real price of zero;
   *  - **listed with `unitPrice: null`** — UNPRICED, nobody has said what it costs;
   *  - **absent from the map** — UNKNOWN, because the read failed or was cut off
   *    at 100.
   *
   * Measured 2026-09-03: of 30 catalogue products **5 carry no `unitPrice` at
   * all and 13 more carry `"0.00"`**, so the two are not a rare pair — a
   * `get() ?? '0.00'` would price 18 of 30 products at free on a forecast
   * somebody bills against, and a load failure would read as a business fact.
   */
  const [prices, setPrices] = useState<Map<string, string | null>>(() => new Map());
  /*
   * Each product's `quantityStep` / `unitLabel` (`@/lib/product-units`), kept
   * for every product any search returned so a narrower search cannot forget
   * a chosen row's step. Absent = unread, which judges no step.
   */
  const [units, setUnits] = useState<ReadonlyMap<string, ProductUnitFields>>(() => new Map());
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);
  const loading = loadedTerm !== term;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listProducts(
        { active: true, perPage: OPTION_PER_PAGE, ...(term ? { search: term } : {}) },
        signal,
      )
      .then((result) => {
        setOptions(
          result.data.map((product) => ({
            value: String(product.id),
            label: product.productName,
            // Three of the catalogue's products carry no code; a `hint: undefined`
            // renders an empty second line, so the key is omitted instead.
            ...(product.productCode ? { hint: product.productCode } : {}),
          })),
        );
        setPrices(new Map(result.data.map((product) => [String(product.id), product.unitPrice])));
        setUnits((current) => {
          const next = new Map(current);
          for (const product of result.data) {
            next.set(String(product.id), {
              ...(product.quantityStep !== undefined ? { quantityStep: product.quantityStep } : {}),
              ...(product.unitLabel !== undefined ? { unitLabel: product.unitLabel } : {}),
            });
          }
          return next;
        });
        setTruncated(result.meta.totalPages > 1);
        setFailed(false);
      })
      .catch(() => {
        /*
         * ⚠️ `failed`, not merely empty — the same distinction the company
         * picker draws. "This catalogue has no products" is a claim; "the
         * request did not come back" is not, and the two look identical in an
         * empty list.
         *
         * ⚠️ The price map is EMPTIED rather than left standing, which is what
         * makes every product read as UNKNOWN rather than as the price the
         * previous search happened to load. An emptied map cannot assert
         * anything; a stale one can.
         */
        if (signal.aborted) return;
        setOptions([]);
        setPrices(new Map());
        setTruncated(false);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
  }, [term]);

  return {
    options,
    prices,
    units,
    loading,
    failed,
    setTerm,
    footnote: truncated ? 'Showing the first 100 matches — keep typing to narrow.' : undefined,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE PROJECT THIS PLAN IS FOR
 * ──────────────────────────────────────────────────────────────────────────── */

interface ProjectsState {
  rows: Project[];
  loading: boolean;
  failed: boolean;
}

/**
 * Every ACTIVE project, PAGED — and the paging is the whole point of this hook
 * rather than a detail of it.
 *
 * 🔴 **`useProjectOptions` AND EVERY OTHER SINGLE-PAGE READ IS THE WRONG TOOL
 * HERE, AND THE DEFECT IT PRODUCES HAS ALREADY BEEN REPORTED ONCE.** There are
 * ~120 active projects and `perPage` caps at **100** — 101 is a 422, so raising
 * it is not available and never was. A single page therefore drops ~20 projects
 * off the END of the alphabet, silently: the response is a clean 200, the list
 * looks complete, and the projects that are missing are the same ones every
 * time. On 2026-09-15 that surfaced as the owner reporting "the projects are
 * not showing up" — all eight projects selling one product sat at name-ranks
 * 106-113, so the picker was **EMPTY**, not merely short, and the app's own
 * "no project sells this" sentence was correctly withheld by a truncation
 * guard, which is what left nothing on screen to report.
 *
 * `api.listAllProjects` reads page after page and resolves with the whole list
 * or throws. ⛔ **Never raise `perPage`** — 100 is the server's own ceiling.
 *
 * ⚠️ **A PARTIAL LIST IS DISCARDED, NEVER RENDERED.** A project list that stops
 * early is indistinguishable from a complete one, and this form REQUIRES a
 * choice from it — so a flaky second page would otherwise let somebody file a
 * plan against the wrong project while the right one was simply absent. Failing
 * closed is what `listAllProjects` throwing is for; the cost is stated at the
 * control, which renders the failure rather than an empty list.
 *
 * ⚠️ **`enabled` is read ONCE at mount and cannot change**, because it is
 * `standaloneEditable`, which is derived from a `row` prop this dialog reads
 * once. So seeding `loading` from it is safe — there is no second render in
 * which the flag flips and leaves the state describing the wrong question.
 */
function useActiveProjects(enabled: boolean): ProjectsState {
  const [rows, setRows] = useState<Project[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    /*
     * An ATTACHED row renders no picker — its project lives on the real
     * document's LINES and the server refuses the key — so the read is not made
     * at all rather than made and discarded.
     */
    if (!enabled) return;
    const controller = new AbortController();

    api
      .listAllProjects(controller.signal, { isActive: true, sort: 'name', order: 'asc' })
      .then((all) => {
        if (controller.signal.aborted) return;
        setRows(all);
        setFailed(false);
      })
      .catch(() => {
        // See the doc above: what arrived is thrown away rather than offered.
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [enabled]);

  return { rows, loading, failed };
}

/**
 * One project as a picker row, with its PERIOD on the second line.
 *
 * ⚠️ **The period disambiguates and is not decoration** — a project's name is
 * NOT unique, because one product genuinely runs twice in a period. Lifted from
 * the quote and invoice line grids' own `toProjectOption` so a reader meets the
 * same shape here that they already know from there; a cycle with no intake
 * spans the whole cycle, which is a real state rather than a missing half, so
 * it renders as the cycle alone.
 */
function projectOption(project: Project): ComboboxOption {
  const period = project.cycle
    ? project.intake
      ? `${project.cycle.name} · ${project.intake.name}`
      : project.cycle.name
    : null;
  return {
    value: String(project.id),
    label: project.name,
    ...(period !== null ? { hint: period } : {}),
  };
}

/**
 * The two pills, DERIVED from the shared union rather than written out — the
 * same constant `CompaniesPage` and `CompanyProjectsModal` each build, for the
 * reason they both state: the labels live in `PROJECT_CYCLE_SCOPE_LABELS`,
 * which is also the vocabulary every other mount of this control reads.
 * Spelling `Current` and `Previous` out here would be a second copy of a word
 * that already has one home, and the drift shows up as one screen calling a set
 * of projects something the next screen calls something else. Mapping the union
 * also means a third scope appears on this control on its own.
 */
const PROJECT_CYCLE_SCOPE_OPTIONS: readonly SegmentedControlOption<ProjectCycleScope>[] =
  PROJECT_CYCLE_SCOPES.map((scope) => ({
    value: scope,
    label: PROJECT_CYCLE_SCOPE_LABELS[scope],
  }));

/* ────────────────────────────────────────────────────────────────────────────
 * THE CURRENCY IS DERIVED FROM THE COMPANY
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * A company's currency as a code this form may actually seed — or `null`, which
 * seeds nothing.
 *
 * ⚠️ **The pattern check is not decoration**, and it is lifted verbatim from
 * `DealFormModal.usableCurrencyCode` and the same test in `QuoteFormModal`. The
 * legal set is XERO's rather than our master's, so a stored code may be in any
 * shape at all; a code that is not exactly three capital letters seeded into
 * this box is a guaranteed 422 on a field the person never touched. Dropping it
 * leaves the picker empty, which is honest and fixable.
 */
function usableCurrencyCode(code: string | null | undefined): string | null {
  if (code === null || code === undefined) return null;
  const upper = code.trim().toUpperCase();
  return upper !== '' && CURRENCY_PATTERN.test(upper) ? upper : null;
}

/** Where a derived currency came from, so the field can name the record to edit. */
type CurrencySource = 'company' | 'billing-region';

interface DerivedCurrency {
  status: 'no-company' | 'reading' | 'read' | 'failed';
  /** `''` means nothing derived — never "no currency applies". */
  code: string;
  source: CurrencySource | null;
  /** The billing region's display name, where that is the rung that answered. */
  sourceName: string | null;
}

const NO_COMPANY: DerivedCurrency = {
  status: 'no-company',
  code: '',
  source: null,
  sourceName: null,
};

/**
 * The chosen company's currency, read the way the quote and invoice forms read
 * it.
 *
 * ⚠️ **TWO RUNGS: the company's OWN currency, falling through to its BILLING
 * REGION's.** That is `InvoiceFormModal`'s chain, not `QuoteFormModal`'s
 * one-rung one, and the difference is the whole reason this was reported as
 * broken. `InvoiceFormModal` records the measurement: **4 of 149 live companies
 * carry a currency of their own.** A company-only rung therefore derives
 * NOTHING on ~97% of accounts — which is exactly the "currency is not getting
 * derived" this hook exists to fix, reimplemented.
 *
 * ⚠️ **The `!== ''` fall-through is what makes the chain right, and a bare `??`
 * over the raw values would be wrong on exactly one row.** A company holding an
 * empty or malformed code must fall THROUGH to its region rather than winning
 * the chain and pricing a plan in no currency. `usableCurrencyCode` returning
 * `null` is what expresses that.
 *
 * ⚠️ **`billingRegionDefaults` ARRIVES ON THE COMPANY READ. Do NOT add a
 * `/billing-region-defaults` fetch here.** The server serves the region's row
 * beside the company's own values, unmerged, precisely so a client can fall
 * back AND still say which record a value came from — which
 * `src/lib/types.ts` requires in as many words: *"A UI FALLING BACK TO ONE OF
 * THESE MUST SAY SO."* `currencyNoteFor` below is that half.
 *
 * ⚠️ **A FAILED read is its own status and is NOT collapsed into "nothing is
 * set".** `InvoiceFormModal` deliberately collapses the two, and it is right to
 * there, because its currency row is READ-ONLY and both answers reduce to the
 * same instruction. Here the box is editable, so the two lead to different
 * sentences: "nobody has set one" sends a person to the company record, and
 * "we could not ask" tells them to type one and move on.
 */
function useCompanyCurrency(companyId: number | null): DerivedCurrency {
  /**
   * The answer, tagged with the company it ANSWERS FOR.
   *
   * Tagged rather than paired with a loading flag, exactly as `loadedTerm` is
   * in the two hooks above: `reading` is then derived during the very render
   * that changed the company, with no setState inside an effect to start a
   * second render pass, and a response for a company nobody is looking at any
   * more can never be read.
   */
  const [answer, setAnswer] = useState<
    (Omit<DerivedCurrency, 'status'> & { forCompanyId: number; failed: boolean }) | null
  >(null);

  useEffect(() => {
    if (companyId === null) return;
    const controller = new AbortController();
    const { signal } = controller;

    api
      .getCompany(companyId, signal)
      .then(({ data: company }) => {
        /*
         * ⚠️ The CODE, never `currency.id`. A tracker row stores `currency` as
         * a three-letter code with no foreign key to `/currencies`, and a
         * company's `currency.id` is nullable precisely because the legal set
         * is Xero's — a stored code may name no row in our master at all.
         * `code` is the only half of that object that is always meaningful.
         */
        const own = usableCurrencyCode(company.currency?.code);
        if (own !== null) {
          setAnswer({
            forCompanyId: companyId,
            code: own,
            source: 'company',
            sourceName: null,
            failed: false,
          });
          return;
        }

        const region = company.billingRegionDefaults;
        const fromRegion = usableCurrencyCode(region?.currencyCode);
        if (fromRegion !== null) {
          setAnswer({
            forCompanyId: companyId,
            code: fromRegion,
            source: 'billing-region',
            /*
             * Display only — `types.ts` says in terms that nothing may ever
             * MATCH on this name, and nothing here does. It exists so a person
             * told a value came from a billing region knows which row on
             * `/catalog` to go and edit.
             */
            sourceName: region?.name ?? null,
            failed: false,
          });
          return;
        }

        setAnswer({
          forCompanyId: companyId,
          code: '',
          source: null,
          sourceName: null,
          failed: false,
        });
      })
      .catch(() => {
        /*
         * ⚠️ An ABORT must record nothing: it fires because a newer read for a
         * DIFFERENT company is already in flight, and recording this one would
         * make that read's field say "couldn't read" while it is still running.
         */
        if (signal.aborted) return;
        setAnswer({
          forCompanyId: companyId,
          code: '',
          source: null,
          sourceName: null,
          failed: true,
        });
      });

    return () => controller.abort();
  }, [companyId]);

  if (companyId === null) return NO_COMPANY;
  if (answer === null || answer.forCompanyId !== companyId) {
    return { status: 'reading', code: '', source: null, sourceName: null };
  }
  return {
    status: answer.failed ? 'failed' : 'read',
    code: answer.code,
    source: answer.source,
    sourceName: answer.sourceName,
  };
}

/**
 * The sentence under the currency box, naming the record the value came from.
 *
 * ⚠️ **This is a REQUIREMENT of the contract, not helper text.**
 * `Company.billingRegionDefaults` in `src/lib/types.ts`: *"A UI FALLING BACK TO
 * ONE OF THESE MUST SAY SO. 'This company is billed in GBP' and 'this company
 * states nothing and its region defaults to GBP' are two different records to
 * go and edit — and somebody shown a value nobody on the account set, with
 * nothing saying whose it is, opens the company record and finds the field
 * empty."*
 *
 * The touched case is said out loud for the mirror reason: once somebody has
 * chosen a currency themselves, the field STOPS following the company, and a
 * person who then changes the company is owed the fact that their choice
 * survived rather than being left to wonder whether it was quietly restamped.
 */
function currencyNoteFor(
  derived: DerivedCurrency,
  mode: PartyMode,
  touched: boolean,
): string {
  if (touched) return 'Set by hand — changing the company will not change it.';
  switch (derived.status) {
    case 'no-company':
      return mode === 'company'
        ? 'Choose a company and its currency fills in here.'
        : 'No company on file to read one from — choose the currency.';
    case 'reading':
      return 'Reading the currency from the company…';
    case 'failed':
      return 'Couldn’t read the company’s currency — choose one.';
    case 'read':
      if (derived.source === 'company') return 'From the company record.';
      if (derived.source === 'billing-region') {
        return derived.sourceName === null
          ? 'From the company’s billing region, which is where it is set.'
          : `From the ${derived.sourceName} billing region — the company sets none of its own.`;
      }
      return 'No currency is set on the company or on its billing region — it comes from one of those.';
  }
}

/**
 * One product on the plan while it is being typed.
 *
 * ⚠️ **`uid` is the React key and the error key, NOT the product id.** A row
 * exists before a product is chosen and two rows can briefly name the same
 * product — which is exactly the state this form refuses — so keying on
 * `productId` would collapse the duplicate onto the row it duplicates and hide
 * the error being reported about it.
 *
 * All three values are STRINGS because they are the raw contents of a control:
 * `productId` is a Combobox value (`''` = nothing chosen), and `quantity` and
 * `amount` are figures the user is midway through typing.
 */
interface ProductDraft {
  uid: number;
  productId: string;
  quantity: string;
  /**
   * What this line is expected to bill — **a FLAT AMOUNT, which `quantity` does
   * NOT multiply.** Owner's decision, 2026-09-03: three at `10000.00` plans ten
   * thousand. Every neighbouring line table holds a UNIT price and generates
   * the total by multiplying, so this is the one thing a reader carries across
   * wrongly, and it is only wrong on lines whose quantity is not 1.
   *
   * `''` is "no amount planned" and is what OMITS the key — never `'0.00'`,
   * which is a real price of zero.
   */
  amount: string;
  /**
   * Whether a person has typed in the amount box — **tracked explicitly, and
   * set even when they EMPTY it.**
   *
   * While it is false the box FOLLOWS the catalogue: choosing a product writes
   * that product's `unitPrice` in. Once it is true the box is the person's and
   * nothing re-seeds it, for the life of this row.
   *
   * ⚠️ **Emptying the box has to count as touching it**, or clearing a seeded
   * figure hands control straight back to the seeder, which re-fills it on the
   * next product change — the field then fights the user. The same reading
   * every `touched` flag in this app takes of a cleared box.
   */
  amountTouched: boolean;
  /**
   * ✅ **WHAT THIS LINE IS FOR, IN THE PLANNER'S WORDS — migration 160.** `''`
   * is "none written" and OMITS nothing: it is sent as an empty string, which
   * the server trims and folds to `null`.
   *
   * ⚠️ **IT IS NOT A NOTE — IT REACHES THE CUSTOMER.** Raising this plan copies
   * it onto the document line's own `description`, which is the text printed on
   * the quote or invoice. **Ref notes on the row above is the private field**;
   * these two are one scroll apart and they go to opposite audiences.
   *
   * ⚠️ **AND IT REPLACES THE DERIVATION RATHER THAN ADDING TO IT.** A document
   * line with no typed description shows `Product — Cities` and keeps it up to
   * date; a seeded one stops following that for ever. So a planned description
   * reading just "Festival" is worse than none at all, because it silently
   * costs the line its city list. Nothing here can warn about that — the cities
   * are not known until the document exists — which is why it is recorded at
   * the field rather than as a hint on screen.
   */
  description: string;
}

/** A refusal about one basket row, and which of its four controls it belongs to. */
interface ProductDraftError {
  field: 'productId' | 'quantity' | 'amount' | 'description';
  message: string;
}

/**
 * What the CATALOGUE says a product costs — as three states that cannot be
 * collapsed into two.
 *
 * ⚠️ **`{ known: false }` IS NOT `{ known: true, amount: null }`.** Unknown is
 * *we never read a price for this product*: the list failed, or was truncated at
 * 100, or the product came back on a row the server seeded rather than off the
 * catalogue. Unpriced is *the catalogue says nobody has priced it*. A single
 * `string | null` cannot hold both, and the `??` that squashes them is exactly
 * how a load failure gets reported as a business fact.
 *
 * A discriminated union rather than an optional property, so neither state can
 * be reached by forgetting to check the other.
 */
type CatalogueAmount = { known: false } | { known: true; amount: string | null };

/**
 * The option that was CHOSEN for a product, and what the catalogue said it cost
 * at that moment — kept together, by product id.
 *
 * ⚠️ **ONE RECORD RATHER THAN TWO, DELIBERATELY.** One search term serves every
 * row, so searching in row three empties the page row one's product was picked
 * from — a Combobox whose value matches no option renders as unset, and a price
 * looked up in that same page would vanish with it. Both halves therefore need
 * pinning, and two parallel records updated in two places would agree the day
 * they were written and drift on the first correction. Written as one, they
 * cannot.
 */
interface PinnedProduct {
  option: ComboboxOption;
  /**
   * ⚠️ `{ known: false }` on a row **seeded from the server in edit mode** —
   * `InvoiceTrackerPlannedProduct` carries the PLANNED amount, which is what
   * somebody recorded, and says nothing about what the catalogue charges. The
   * two are different questions and this one has no answer there.
   */
  catalogue: CatalogueAmount;
}

/**
 * What went wrong on save, said in full.
 *
 * ⚠️ Deliberately NOT `formMessage ?? message` — a 422's top-level message is
 * the generic "Validation failed" and the actionable half lives in the details,
 * so both can be true at once and coalescing silently drops one.
 */
function describeSaveFailure(error: ApiError, unroutable: readonly string[]): string[] {
  const lines: string[] = [];
  for (const line of [error.message, ...unroutable, ...error.detailReasons]) {
    if (line && !lines.includes(line)) lines.push(line);
  }
  return lines;
}

export interface FutureDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The row being EDITED. Omit — or pass `undefined` — to plan a new one.
   *
   * ⚠️ **OPTIONAL, AND THAT IS THE CONTRACT RATHER THAN CONVENIENCE.** Every
   * existing caller opens this dialog to create, and adding a required prop
   * would make each of them a compile error for a mode they do not use.
   *
   * ⚠️ **A STANDALONE ROW IS THE ONLY THING THIS FORM CAN FULLY EDIT.** On an
   * ATTACHED row (`source` `'invoice'` or `'quote'`) the party, currency, amount
   * and products are the DOCUMENT'S, read through it at query time — the server
   * refuses all four against its `STANDALONE_ONLY_KEYS`, so those controls are
   * not rendered at all. Hiding them is what stops the keys being sent: there is
   * no value of `products` meaning "this row has none of its own".
   *
   * ⚠️ **It is READ ONCE, at mount.** The dialog unmounts while closed, so a row
   * is seeded on opening and never re-seeded — swapping this prop while `open`
   * would leave every control holding the previous row's values, silently.
   * Close and reopen instead.
   */
  row?: InvoiceTrackerRow;
  /**
   * Fired after a successful write, before `onClose`. Re-read the list here.
   *
   * ⚠️ **It fires after an EDIT as well as a create**, and the name is kept
   * because renaming it would be a breaking change to callers this change does
   * not own. What it means is "the list is out of date", which is true of both.
   *
   * ⚠️ **The new row is deliberately NOT handed back for a caller to splice
   * in.** Where it belongs on screen is the server's answer, not this dialog's:
   * the list is sorted, paginated and narrowed by a financial year and a set of
   * filters, so a row inserted locally would sit outside the server's own
   * ordering — and one that does not match the active filters would appear on a
   * page it has no business being on. Both look like the write worked and are
   * wrong the moment anything re-reads.
   */
  onCreated: () => void;
}

/**
 * Unmounted while closed rather than held at `open={false}`, so every piece of
 * state is seeded on mount and the company read happens once per opening. There
 * is no reset effect to forget a field in, and reopening after cancelling is a
 * fresh mount — which is what somebody expects after abandoning a form.
 */
export function FutureDocumentDialog({ open, ...rest }: FutureDocumentDialogProps) {
  if (!open) return null;
  return <FutureDocumentForm {...rest} />;
}

function FutureDocumentForm({ row, onClose, onCreated }: Omit<FutureDocumentDialogProps, 'open'>) {
  const { ability } = useAuth();

  /**
   * The row being edited, or `null` for a create.
   *
   * ⚠️ **`editing === null` IS THE ONE TEST, and every branch below reads it
   * rather than a second `isEdit` flag.** Two spellings of one question is how
   * half a form ends up in the other mode.
   */
  const editing = row ?? null;

  /**
   * Whether the STANDALONE-ONLY half of this form may be shown and sent.
   *
   * ⚠️ **A CREATE is always standalone** — this dialog posts nothing else. An
   * EDIT is standalone only where the row says so; on an attached row the party,
   * currency, amount and basket belong to the document and the server refuses
   * every one of them. **Hiding the controls is the guard**, not remembering to
   * omit the keys: a control that does not render cannot be touched, and only a
   * touched control puts a key on the wire.
   */
  const standaloneEditable = editing === null || editing.source === 'standalone';

  /*
   * ⚠️ **NO PRESELECTED KIND, AND THE EMPTY STRING IS THE POINT.**
   *
   * The server DEFAULTS an omitted `kind` to `'invoice'`, which is the true
   * answer for every row written before the column existed — but it makes a
   * preselected control dangerous in a way an ordinary default is not: a person
   * who does not notice the field files a planned quote as a planned invoice,
   * receives a 201, and the row is on the tracker looking entirely correct.
   *
   * ⚠️ And it cannot be corrected afterwards. `kind` is absent from the server's
   * update schema, so a PATCH naming it is a 422 — the only fix is to delete the
   * row and make another, losing whatever was typed against it and the reminder
   * task raised with it. A field that cannot be edited must be CHOSEN.
   *
   * So this starts empty, `validate()` refuses empty, and the request always
   * carries an explicit value — the default is never relied on.
   *
   * 🔴 **ON AN EDIT IT IS SEEDED AND EDITABLE on a standalone row (2026-10-11).**
   * *SUPERSEDED: "ON AN EDIT IT IS SEEDED AND NOT EDITABLE, because `kind` is
   * absent from the server's update schema entirely."* `buildPatch` sends it only
   * when it changed. The paragraph above about corrections is history too: a
   * mistyped kind is now fixed in place, though a create still starts empty so
   * the server's `'invoice'` default is never relied on.
   */
  const [kind, setKind] = useState<InvoiceTrackerKind | ''>(() => editing?.kind ?? '');

  /*
   * ⚠️ **THE PARTY SEED READS `company`, NEVER `partyName`, TO DECIDE THE
   * MODE.** `row.partyName` is ALWAYS present — it is the COMPANY'S name on a
   * company-backed row — so seeding the typed box from it would put a company's
   * name in the "someone not on file" box and, on the first save, write it into
   * a column that is meant to hold a name for a party with no company at all.
   */
  const [mode, setMode] = useState<PartyMode>(() =>
    editing !== null && editing.company === null ? 'name' : 'company',
  );
  const [companyId, setCompanyId] = useState(() =>
    editing?.company ? String(editing.company.id) : '',
  );
  const [partyName, setPartyName] = useState(() =>
    editing !== null && editing.company === null ? editing.partyName : '',
  );

  /*
   * ⚠️ **THE PROJECT THIS PLAN IS FOR — ONE PER ROW, NEVER ONE PER LINE.**
   * Owner's decision: a plan names a single project and its whole basket is for
   * it, which is why `InvoiceTrackerPlannedProduct` carries no project of its
   * own and must not gain one. It is a property of the plan like the company and
   * the currency, which is why the control sits up here rather than in the
   * basket.
   *
   * ⚠️ **IT IS WHY THE FEATURE EXISTS AT ALL.** The sales-pursuit sync advances
   * a card only when a raised document names its `(company, project)` pair, so
   * a plan carrying no project raises a perfectly correct document that advances
   * NOTHING — a `201`, no error on either side of the wire, and a silent no-op.
   *
   * 🔴 **REQUIRED ON CREATE WHILE THE SERVER STILL ACCEPTS AN OMITTED KEY, AND
   * THAT IS DELIBERATE RATHER THAN PREMATURE.** The owner decided on 2026-09-17
   * that `POST` will refuse a plan naming no project; this form refuses one
   * first. **The precedent is `plannedRaiseFrom` immediately below** — `required`
   * here for as long as anyone can remember while the server's own schema has it
   * as `optionalDate()`. Being ahead of the wire costs nothing in the direction
   * that matters: the form asks for something the API will take either way.
   *
   * ⛔ **AND `PATCH` KEEPS IT OPTIONAL, WHICH IS WHAT KEEPS 17 EXISTING ROWS
   * EDITABLE.** The rows imported before this column existed name no project,
   * and the owner intends to fill them in by hand through this very dialog —
   * so the EDIT mount must let somebody fix a typo in the Ref notes of one
   * without being made to assign a project first, and must equally let them
   * assign one. See `validate()`, where that rule is written out.
   */
  const [projectId, setProjectId] = useState(() =>
    editing?.project ? String(editing.project.id) : '',
  );
  /**
   * Whether the person has CHOSEN a project on this visit.
   *
   * ⚠️ **It is what makes the requirement fire on a create and stand down on a
   * legacy edit**, and it is set only by the picker's own `onChange`. Seeded
   * `false` in both modes: a row that arrived carrying a project has not been
   * touched by anybody, and a row that arrived carrying none must not be treated
   * as though somebody had just cleared it.
   */
  const [projectTouched, setProjectTouched] = useState(false);

  /*
   * ⚠️ **THE CURRENCY IS DERIVED, NOT LOCKED — and these two pieces of state are
   * the whole of that rule.** `chosenCurrency` holds a value somebody typed
   * here and NOTHING ELSE; the derivation lives in `useCompanyCurrency` and is
   * never written into state at all. `effectiveCurrency` below picks between
   * them during render.
   *
   * That answers the two states a derived field has to get right, and it
   * answers them by construction rather than by an effect remembering to check
   * a flag:
   *
   *  - **A person's override survives a company change.** Once
   *    `currencyTouched` is true the derivation is ignored for the life of the
   *    dialog, so re-pointing the plan at another company cannot silently stamp
   *    over a currency somebody chose. The note under the box says so.
   *  - **An UNTOUCHED field follows the company wholesale, empty included.** A
   *    company that derives nothing CLEARS the box rather than leaving the
   *    previous company's answer standing beside the new party — a stale claim
   *    nobody can see is stale. It stays a live picker throughout, so "nothing
   *    derived" is always one click from being answered.
   *
   * ⚠️ **Derived during render rather than seeded by an effect.** The three
   * document forms use a `currencyTouched` ref plus a `setValues` inside the
   * company effect because they hold one big `values` object that a prefill and
   * a dozen other seeds also write to. This form holds one string, so the
   * simpler shape is available — and it is the one oxlint's
   * `set-state-in-effect` rule asks for: no cascading render, and no window in
   * which the state and the picker disagree.
   *
   * ⚠️ **ON AN EDIT BOTH ARE SEEDED AS ALREADY-CHOSEN, WHICH SWITCHES THE
   * DERIVATION OFF FOR GOOD.** A stored currency is the ROW'S OWN and must never
   * be re-derived: re-pointing an existing plan at another company would
   * otherwise restamp a figure somebody set, on a record that already exists.
   * The same rule the quote and invoice forms follow — seed on create, never on
   * an existing document. A row storing no currency seeds `''`, which is an
   * ordinary empty picker rather than an invitation to derive one.
   */
  const [chosenCurrency, setChosenCurrency] = useState(() => editing?.currency ?? '');
  const [currencyTouched, setCurrencyTouched] = useState(() => editing !== null);

  /**
   * Whether the row ARRIVED with its total derived from priced lines.
   *
   * ⚠️ **It decides what the typed amount box could be seeded with, and the
   * answer is sometimes NOTHING.** `row.amount` is the server's DERIVED figure
   * whenever any planned line carries an amount — the stored typed column is not
   * on the wire at all in that case — so seeding the box from it would put a sum
   * into a control that writes a different column, which is the proxy-field
   * shape exactly: a value written to answer one question, read as the answer to
   * another. It seeds `''` instead, and `buildPatch` knows the seed was
   * unreadable rather than empty.
   */
  const derivedAtOpen = editing !== null && editing.products.some((line) => line.amount !== null);

  const [amount, setAmount] = useState(() =>
    editing === null || derivedAtOpen ? '' : editing.amount,
  );
  const [plannedRaiseFrom, setPlannedRaiseFrom] = useState(
    () => editing?.plannedRaiseFrom ?? '',
  );
  /*
   * ⚠️ **"Ref notes" IS THE ROW'S `comment` FIELD. THERE IS NO SEPARATE COLUMN,
   * AND ONE MUST NOT BE ADDED.** The owner's words for this box are "ref notes";
   * the wire key is `comment`, which is the tracker row's only prose field and
   * the same one the tracker's own Comment column renders and edits. They are
   * one value under two labels — so a second box beside this one would be a
   * second answer to one question, and whichever screen wrote last would win
   * silently.
   */
  const [refNotes, setRefNotes] = useState(() => editing?.comment ?? '');

  /*
   * ⚠️ **THE PLANNED BASKET, AND IT STARTS EMPTY AND UNTOUCHED — WHICH IS WHY
   * THE KEY IS OMITTED RATHER THAN SENT AS `[]`.**
   *
   * `products` is a WHOLE-SET REPLACE on the wire, so `[]` CLEARS and an absent
   * key LEAVES ALONE. Those are the same thing on a create, where there is
   * nothing to clear — but they stop being the same thing the moment this shape
   * is reused for an edit, and `InvoiceTrackerStandaloneInput` is the type
   * `api.updateInvoiceTrackerRow` takes. So the rule is written into `buildBody`
   * here rather than left as a property of this dialog only creating: **the key
   * is sent only when there is a basket to send.**
   *
   * ⚠️ *SUPERSEDED 2026-09-03: "**This form is STANDALONE-ONLY, which is what
   * makes the editor safe to render unconditionally.** It has no `row` prop and
   * posts a new standalone row every time … **If this dialog ever gains an edit
   * mode, the editor must be HIDDEN for an attached row**."* **It has gained
   * one, and that instruction was followed rather than discovered** — see
   * `standaloneEditable`, which is what the editor and every other
   * standalone-only control render behind.
   */
  /*
   * The (product, quantity) pairs the plan already stores. A stored pair from
   * before a product's step existed is legacy and is not judged against it.
   */
  const [storedPlanPairs] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        (editing?.products ?? []).map((planned) => `${planned.productId}|${planned.quantity}`),
      ),
  );
  const [productDrafts, setProductDrafts] = useState<ProductDraft[]>(() =>
    (editing?.products ?? []).map((planned, index) => ({
      uid: index + 1,
      productId: String(planned.productId),
      quantity: planned.quantity,
      /*
       * `null` — nobody priced this line — seeds an EMPTY box, never `'0.00'`.
       * An empty box omits the key, which is the same instruction `null` was.
       */
      amount: planned.amount ?? '',
      /*
       * ⚠️ **TOUCHED, because a STORED figure is somebody's decision.** A
       * seeded line must not re-follow the catalogue: changing its product
       * would otherwise overwrite an amount a person recorded with whatever the
       * catalogue happens to charge, on a plan that already exists. The same
       * rule everywhere else here — seed on create, never re-derive on a record
       * that exists.
       */
      amountTouched: true,
      /*
       * `null` — nobody wrote one — seeds an EMPTY box, exactly as the amount
       * above does. There is no `descriptionTouched` twin and there must not
       * be: nothing ever re-derives this box from the catalogue, so it has no
       * seeder to fight. The flag beside it exists only because the AMOUNT
       * follows the product until somebody takes it over.
       */
      description: planned.description ?? '',
    })),
  );
  /**
   * ⚠️ **WHETHER ANYBODY HAS EDITED THE BASKET AT ALL — and it is the guard
   * against the one write on this contract that destroys data silently.**
   *
   * `products` is a WHOLE-SET REPLACE: `[...]` replaces, `[]` CLEARS, and an
   * absent key LEAVES ALONE. On a PATCH the destructive value is the one a form
   * produces by accident — a basket nobody opened serialises to `[]`, wipes a
   * plan somebody typed, and answers 200 with nothing to notice. So the key is
   * sent **only when this is true**, and this is set **only** by
   * {@link editBasket}, which is the only writer of `productDrafts`.
   *
   * It therefore cannot be `[]` unless somebody actually emptied the list.
   */
  const [basketTouched, setBasketTouched] = useState(false);
  /** Row identity, issued once each. See {@link ProductDraft.uid}. */
  const nextDraftUid = useRef((editing?.products.length ?? 0) + 1);
  const [productErrors, setProductErrors] = useState<Record<number, ProductDraftError>>({});
  /**
   * The option that was CHOSEN for a product, and what the catalogue charged for
   * it at that moment, kept by id. See {@link PinnedProduct}.
   *
   * One search term serves every row, so searching in row three empties the
   * option list row one's product was picked from — and a Combobox whose value
   * matches no option renders as unset. Pinning the chosen option back keeps
   * every row showing what it holds, and gives the refusals below a product NAME
   * to say instead of a number.
   *
   * ⚠️ **A row seeded from the server pins its OPTION and no price.** The row
   * names the product and carries the amount somebody PLANNED; what the
   * catalogue charges was never read, so the entry says `{ known: false }`
   * rather than inventing one.
   */
  const [pinnedProducts, setPinnedProducts] = useState<Record<string, PinnedProduct>>(() => {
    const seeded: Record<string, PinnedProduct> = {};
    for (const planned of editing?.products ?? []) {
      seeded[String(planned.productId)] = {
        option: {
          value: String(planned.productId),
          label: planned.productName,
          // As the catalogue picker builds it — an absent code omits the key
          // rather than rendering an empty second line.
          ...(planned.productCode ? { hint: planned.productCode } : {}),
        },
        catalogue: { known: false },
      };
    }
    return seeded;
  });

  const [saving, setSaving] = useState(false);
  /** A refusal bound to one control, keyed by the server's own path. */
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  /**
   * Everything the form cannot mark: a 403 (no `details` at all), an
   * unrecognised key, a network failure. Rendered as a banner ALONGSIDE the
   * field errors rather than instead of them — both can be true at once.
   */
  const [formMessages, setFormMessages] = useState<readonly string[]>([]);

  const companies = useCompanyOptions();
  const catalogue = useProductOptions();
  const currencies = useCurrencyOptions();
  /*
   * ⚠️ **BOTH READS ARE GATED ON `standaloneEditable`, for the same reason the
   * company-currency lookup above is gated.** An attached row renders no project
   * picker, so a request whose answer could only be discarded is not made.
   */
  const projects = useActiveProjects(standaloneEditable);
  /*
   * Which cycles count as "Current" — which in this app's vocabulary means
   * current AND upcoming, exactly as the line grids' pills read it.
   *
   * 🔴 *SUPERSEDED, on the owner's instruction of 2026-09-17 — "in the project
   * dropdown make a current and previous switch and keep the current as
   * default": "Used ONLY to order the list, never to narrow it: every active
   * project is offered here."* This index now decides what each PILL offers,
   * exactly as it does on the surfaces that already carry the control. The list
   * was current-first and flat, which reads as one undifferentiated run of ~120
   * rows; it is two lists now.
   *
   * ⛔ **IT STILL DECIDES NOTHING ABOUT WHAT MAY BE SAVED.** The server has no
   * opinion whatever about a project's cycle, and a plan already naming a closed
   * one keeps it — see `storedProjectOption`, which pins the held value into
   * every pill for exactly that reason.
   *
   * ⚠️ **A `null` member is NOT "nothing is current" — it is "the question could
   * not be answered", and `projectInCycleScope` reads it as OFFER EVERYTHING.**
   * So an unread or failed `/cycles` puts every active project under BOTH pills
   * rather than emptying one of them, which is the honest degradation: the split
   * is a convenience and the list is the feature. **Withholding every option
   * because a second request failed would be far worse than offering too many.**
   */
  const cycleScopes = useCycleScopeIds(standaloneEditable);

  /** The project the row ARRIVED holding, or `null`. Never re-read after mount. */
  const storedProject = editing?.project ?? null;

  /**
   * Which pill the reader is looking at.
   *
   * ⚠️ **`DEFAULT_PROJECT_CYCLE_SCOPE`, never a literal `'current'`.** It is the
   * one copy of the owner's default and is shared with every other pill in the
   * app; a literal here is a second copy that would not move when that one does.
   */
  const [scope, setScope] = useState<ProjectCycleScope>(DEFAULT_PROJECT_CYCLE_SCOPE);
  /**
   * Whether the reader has CHOSEN a pill on this visit.
   *
   * The same rule `projectTouched` and `currencyTouched` already run on here,
   * for the same reason: the opening pill is a SEED, so it has to stop following
   * the data the instant somebody overrules it. Without this, pressing
   * `Previous` while `/projects` is still in flight would be undone by the seed
   * landing a moment later — a control that visibly reverts itself.
   */
  const [scopeTouched, setScopeTouched] = useState(false);

  /**
   * The pill that OFFERS the project this row arrived holding — or `null` when
   * no pill is more right than the default.
   *
   * 🔴 **THIS IS WHY AN EDIT DOES NOT OPEN ON AN EMPTY REQUIRED FIELD.** Owner's
   * decision, 2026-09-17: editing a plan whose project sits in a closed cycle
   * opens on the pill that holds it. A `Combobox` renders its PLACEHOLDER for a
   * value absent from its options, so a picker narrowed to `Current` over a
   * `Previous` project would read as *no project chosen* on a field this form
   * marks required — and the obvious repair is to pick a different project,
   * which silently repoints somebody's plan.
   *
   * ⚠️ **It asks which pill OFFERS the project, NOT which cycle the project is
   * in**, and the two differ in exactly the case that matters: while `/cycles`
   * is unread every pill offers everything, so the answer is the default and the
   * control must not move. Walking `PROJECT_CYCLE_SCOPES` in order is what gives
   * the default that precedence, because `current` reads first.
   *
   * `null` in three shapes, all of them meaning *leave the default alone*: a
   * create, a project list that has not arrived, and a project the list does not
   * carry — the last because such a project is PINNED into every pill and is
   * therefore already visible under whichever one is showing.
   */
  const heldProjectScope = useMemo<ProjectCycleScope | null>(() => {
    if (storedProject === null) return null;
    const held = projects.rows.find((project) => project.id === storedProject.id);
    if (held === undefined) return null;
    return (
      PROJECT_CYCLE_SCOPES.find((candidate) => projectInCycleScope(held, cycleScopes[candidate])) ??
      null
    );
  }, [projects.rows, cycleScopes, storedProject]);

  /*
   * ── SEEDING THE PILL, DURING RENDER RATHER THAN IN AN EFFECT ───────────────
   *
   * Both halves of the answer arrive late — the project list over one round
   * trip, the cycle index over another — so the opening pill cannot be seeded in
   * `useState`. Adjusting state during render is React's own answer to deriving
   * state from data that changed: React re-runs this component immediately,
   * before anything is committed, so the first frame that CAN show the right
   * pill does. An effect would paint `Current` over a `Previous` project for a
   * frame first, which is the blank-looking required field this exists to stop.
   *
   * It converges in one extra render — the next `heldProjectScope` equals
   * `scope`, so the test is false — and it can never run at all on a create,
   * where `heldProjectScope` is `null` for the life of the dialog.
   *
   * ⚠️ **It stops at the first touch and never resumes** — see `scopeTouched`.
   */
  if (!scopeTouched && heldProjectScope !== null && heldProjectScope !== scope) {
    setScope(heldProjectScope);
  }

  /**
   * The stored project as ONE picker row, ready to be pinned into whichever
   * pills do not already list it.
   *
   * 🔴 **THE PIN IS THE ONE THING HERE THAT PROTECTS LIVE DATA, AND THE PILLS
   * MADE IT MATTER TWICE OVER.** A `Combobox` falls back to its PLACEHOLDER for
   * a value absent from its options, so a row naming a since-retired project —
   * or, now, one merely filed under the other pill — would render as *no project
   * chosen* over a real stored id, and the next save of any unrelated field
   * would then either leave it standing invisibly or, on a form that diffed
   * against what was on screen, repoint it. `withStoredOption` solves exactly
   * this for the reference masters and its pattern is mirrored rather than
   * called, because that helper takes `ReferenceRecord[]` and cannot carry the
   * period hint every other row here shows.
   *
   * ⚠️ **A project the list DOES carry is pinned AS ITSELF, period hint and
   * all.** It is `projectOption`'s own row, so the pin under the other pill
   * reads identically to its entry under its own, and its PERIOD is what tells
   * the reader which cycle it is from. Hinting it "no longer offered" there
   * would be a flat falsehood about an active project — which is the one thing a
   * hint on a pinned row must never be.
   *
   * ⚠️ **"no longer offered" IS CLAIMED ONLY OVER A LIST THAT REALLY LOADED**,
   * which is `withStoredOption`'s own `knownRetired` rule. While the read is in
   * flight or has failed, the value's absence from an empty list proves nothing
   * — so it is pinned with NO hint rather than accused of being retired for the
   * frames before the rows arrive.
   */
  const storedProjectOption = useMemo<ComboboxOption | null>(() => {
    if (storedProject === null) return null;
    const held = projects.rows.find((project) => project.id === storedProject.id);
    if (held !== undefined) return projectOption(held);
    return {
      value: String(storedProject.id),
      label: storedProject.name,
      ...(projects.rows.length > 0 ? { hint: 'no longer offered' } : {}),
    };
  }, [projects.rows, storedProject]);

  /**
   * Every active project split into one option list per pill, with the stored
   * value pinned into both.
   *
   * ⚠️ **`projectOptionsByScope`, NOT a partition written out here.** The three
   * line grids, `CompaniesPage` and `CompanyProjectsModal` all need the same
   * split with the same pin; copies would agree the day they were written and
   * drift on the first correction, and **the drift shows up as nothing at all on
   * screen** — a picker quietly missing the value its own row holds.
   *
   * ⚠️ **GROUPED BY PILL, NOT SECTIONED IN ONE LIST, AND THAT IS THE CONTROL'S
   * LIMIT RATHER THAN A PREFERENCE.** `Combobox` has no section concept — an
   * option is a `value`, a `label` and a `hint` — which is precisely why the
   * pills exist: they are the grouping the list itself cannot express, and
   * inventing one here would be a new primitive born on a dialog. The period in
   * each row's hint still names the cycle within a pill.
   *
   * ⚠️ **`projects.rows` stays the full list and is never narrowed** — see
   * `useActiveProjects`, which pages it in precisely so the tail of the alphabet
   * is reachable. This splits the options; it does not re-fetch or shorten them.
   *
   * `counts` EXCLUDES the pin, which is the number {@link projectScopeFootnote}
   * needs — counting it would suppress the sentence in the one case it exists
   * for.
   */
  const projectPicker = useMemo(
    () => projectOptionsByScope(projects.rows, cycleScopes, projectOption, storedProjectOption),
    [projects.rows, cycleScopes, storedProjectOption],
  );

  /**
   * What the picker says when the pill the reader is on has nothing in it.
   *
   * ⛔ **A FOOTNOTE IS A FACT ABOUT THE DATA — the documented exception to the
   * no-helper-text rule, the same standing as "Showing the first 100 of 400" —
   * and it is the ONLY sentence these pills are allowed to produce.** "No
   * projects" and "no projects in Previous" are different sentences, and only
   * the second tells the reader to press the other pill; without it an empty
   * list reads as a failed fetch, and this control is the first thing on this
   * form that can empty the list for a reason one click fixes.
   *
   * ⚠️ **It says nothing when BOTH pills are empty**, which is exactly the
   * loading and failed states — so it can never contradict the `Field` error
   * below, which owns the failed read and is the sentence that matters there.
   */
  const projectScopeNote = projectScopeFootnote(scope, projectPicker.counts);

  /*
   * ⚠️ **The lookup is keyed on the MODE as well as the id.** Switching to
   * "A new company" (renamed 2026-10-11 from "Someone not on file", owner's
   * request; it still records a typed name only and creates no company) leaves `companyId` cleared by its own handler, but
   * reading the mode here too means the derivation can never describe a company
   * that is no longer the party — there is no ordering of two state updates in
   * which a stale code is briefly shown as this plan's currency.
   */
  /*
   * ⚠️ **AND ON THE MODE OF THE DIALOG.** An EDIT never derives — `currencyTouched`
   * is seeded true — so asking the server for a company's currency there would
   * be a request whose answer is discarded on arrival. `null` here is what stops
   * it being made at all, rather than made and ignored.
   */
  const lookupCompanyId =
    editing === null && mode === 'company' && companyId !== '' ? Number(companyId) : null;
  const derivedCurrency = useCompanyCurrency(lookupCompanyId);

  /**
   * What this plan is priced in: the person's own choice once they have made
   * one, otherwise whatever the company derives. See `chosenCurrency`.
   */
  const effectiveCurrency = currencyTouched ? chosenCurrency : derivedCurrency.code;

  const currencyPickerOptions = useMemo<ComboboxOption[]>(
    () => currencyOptions(currencies.currencies, effectiveCurrency || null),
    [currencies.currencies, effectiveCurrency],
  );

  /**
   * What the planned LINES add up to, or `null` when not one of them is priced.
   *
   * ⚠️ **`sumDecimalStrings`, NEVER `Number()` AND NEVER A FLOAT.** These are
   * `DECIMAL(14,2)` strings on both sides of the wire and this is money on a
   * finance forecast; the whole reason the wire uses strings is that a float
   * round trip rounds where nobody chose to.
   *
   * ⚠️ **`null` IS "NOTHING TO ADD", NOT A TOTAL OF ZERO**, which is what makes
   * it the switch below. It skips a `null`-shaped entry rather than counting it
   * as zero — an unpriced line contributes nothing and is not itself a zero —
   * and it skips a half-typed `12.` rather than poisoning the sum with `NaN`;
   * `validateProducts` refuses that separately, on the row it belongs to.
   */
  const plannedTotal = useMemo(
    () =>
      sumDecimalStrings(
        productDrafts.map((draft) => (draft.amount.trim() === '' ? null : draft.amount.trim())),
      ),
    [productDrafts],
  );

  /**
   * ⚠️ **EXACTLY ONE FIGURE IS EVER THE ROW'S ANSWER — owner's decision,
   * 2026-09-03 — and this is where that is decided.** The sum of the line
   * amounts whenever any line is priced; the typed box only when none is. So
   * this is a SWITCH between two answers to one question rather than a fallback
   * that drops one of two true things.
   *
   * ⚠️ **The server derives the same thing** (`STANDALONE_AMOUNT_SQL`, migration
   * 147), so this is a preview of an answer the API owns — and the typed value
   * is deliberately NOT written when the derivation is on. Sending it would make
   * this form a SECOND writer of a figure the server computes, and the copy
   * would go stale the moment a line amount changed.
   */
  const amountIsDerived = plannedTotal !== null;
  /** What the Expected amount box SHOWS, whichever of the two is the answer. */
  const shownAmount = plannedTotal ?? amount;

  /** The server judged a different value; its verdict no longer describes this box. */
  function clearError(key: FieldKey) {
    setFieldErrors((current) => {
      if (current[key] === undefined) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  /** The server judged a different basket row; its verdict no longer describes it. */
  function clearProductError(uid: number) {
    setProductErrors((current) => {
      if (current[uid] === undefined) return current;
      const next = { ...current };
      delete next[uid];
      return next;
    });
  }

  /**
   * ⚠️ **THE ONLY WRITER OF `productDrafts`, AND THAT IS THE GUARD RATHER THAN
   * A CONVENIENCE.** Every basket edit goes through here, so `basketTouched`
   * cannot be true without an edit and cannot be false after one — which is what
   * makes it impossible for a PATCH to send `products: []` (the CLEAR) because a
   * basket was merely rendered. **Do not call `setProductDrafts` anywhere else**;
   * the seed in `useState` above is the one other place it is written, and that
   * runs before anybody has touched anything.
   */
  function editBasket(update: (current: ProductDraft[]) => ProductDraft[]) {
    setProductDrafts(update);
    setBasketTouched(true);
  }

  /**
   * What the catalogue says a product costs — the live page first, then whatever
   * was recorded when it was chosen, then UNKNOWN.
   *
   * ⚠️ **`has()`, never `get() ?? null`.** A listed `null` is UNPRICED and a
   * missing entry is UNKNOWN, and the `??` that squashes them reports a
   * truncated or failed catalogue read as a business fact. See
   * {@link CatalogueAmount}.
   */
  function catalogueAmountFor(productId: string): CatalogueAmount {
    if (catalogue.prices.has(productId)) {
      return { known: true, amount: catalogue.prices.get(productId) ?? null };
    }
    return pinnedProducts[productId]?.catalogue ?? { known: false };
  }

  function addProductRow() {
    const uid = nextDraftUid.current;
    nextDraftUid.current += 1;
    editBasket((current) => [
      ...current,
      // `description: ''` is "none written", which the payload sends as `null`.
      // It has no `touched` twin because nothing ever seeds it — see
      // `setDraftDescription`.
      { uid, productId: '', quantity: '', amount: '', amountTouched: false, description: '' },
    ]);
    // A new empty row cannot be what the server refused a moment ago, and a
    // basket-level message left standing beside it reads as being about it.
    clearError('products');
  }

  function removeProductRow(uid: number) {
    editBasket((current) => current.filter((draft) => draft.uid !== uid));
    clearProductError(uid);
    clearError('products');
  }

  function setDraftProduct(uid: number, value: string) {
    const option = catalogue.options.find((candidate) => candidate.value === value);
    const priced = catalogueAmountFor(value);
    // Recorded AT SELECTION, from the list it was selected from — the only
    // moment this component is certain what the person saw, and the only moment
    // the catalogue is certain to hold the price. Re-selecting a row's own
    // pinned option finds nothing here and leaves the existing pin standing,
    // which is what keeps the price recorded with it.
    if (option) {
      setPinnedProducts((current) => ({ ...current, [value]: { option, catalogue: priced } }));
    }
    editBasket((current) =>
      current.map((source) => {
        if (source.uid !== uid) return source;
        /*
         * A product sold in multiples starts at its step (`defaultQuantityFor`)
         * while the box is blank or still holds the previous product's step;
         * every other product keeps the blank that means 1.
         */
        const previousDefault = defaultQuantityFor(catalogue.units.get(source.productId));
        const typed = source.quantity.trim();
        const followsDefault = typed === '' || (previousDefault !== '1' && typed === previousDefault);
        const nextDefault = defaultQuantityFor(catalogue.units.get(value));
        const draft = followsDefault
          ? { ...source, quantity: nextDefault === '1' ? '' : nextDefault }
          : source;
        // A box somebody has typed in stops following the catalogue for good —
        // see `ProductDraft.amountTouched`.
        if (draft.amountTouched) return { ...draft, productId: value };
        /*
         * ⚠️ **AN EMPTY BOX FOR BOTH `null`-SHAPED ANSWERS, AND NEVER
         * `'0.00'`.** An UNPRICED product and one the catalogue never told us
         * about both mean "no amount is planned here", which is what an empty
         * box says and what omitting the key sends. Writing a zero would say the
         * line is free, which is what `"0.00"` means and what only a product
         * genuinely listed at zero may seed.
         */
        const seeded = priced.known && priced.amount !== null ? priced.amount : '';
        return { ...draft, productId: value, amount: seeded };
      }),
    );
    clearProductError(uid);
    clearError('products');
  }

  function setDraftQuantity(uid: number, value: string) {
    editBasket((current) =>
      current.map((draft) => (draft.uid === uid ? { ...draft, quantity: value } : draft)),
    );
    clearProductError(uid);
  }

  function setDraftAmount(uid: number, value: string) {
    editBasket((current) =>
      current.map((draft) =>
        // ⚠️ `amountTouched` is set on EVERY keystroke including the one that
        // empties the box — see `ProductDraft.amountTouched` for why a cleared
        // box must not hand control back to the seeder.
        draft.uid === uid ? { ...draft, amount: value, amountTouched: true } : draft,
      ),
    );
    clearProductError(uid);
  }

  /**
   * The line description (migration 160).
   *
   * ⚠️ **NO `touched` FLAG, DELIBERATELY, AND THE ABSENCE IS THE POINT.** Its
   * neighbour sets `amountTouched` on every keystroke because the amount box
   * FOLLOWS the catalogue until somebody takes it over. Nothing ever seeds or
   * re-derives this box, so there is no seeder to fight and a flag would be
   * state that means nothing — the kind that later reads as an oversight and
   * gets "fixed" into a behaviour nobody asked for.
   *
   * ⚠️ It goes through `editBasket` like every other basket write, so the
   * whole-set-replace guard applies: typing here marks the basket touched and
   * is what makes `products` reach a PATCH at all.
   */
  function setDraftDescription(uid: number, value: string) {
    editBasket((current) =>
      current.map((draft) => (draft.uid === uid ? { ...draft, description: value } : draft)),
    );
    clearProductError(uid);
  }

  /** What a refusal calls this row: the product where one is chosen, else its position. */
  function productLabel(draft: ProductDraft, index: number): string {
    return pinnedProducts[draft.productId]?.option.label ?? `Row ${index + 1}`;
  }

  /**
   * The options one row offers — the current page, with this row's own choice
   * pinned back on when the page no longer contains it. See `pinnedProducts`.
   */
  function optionsForDraft(draft: ProductDraft): ComboboxOption[] {
    const pinned = pinnedProducts[draft.productId];
    if (draft.productId === '' || pinned === undefined) return catalogue.options;
    if (catalogue.options.some((option) => option.value === draft.productId)) {
      return catalogue.options;
    }
    return [pinned.option, ...catalogue.options];
  }

  /**
   * The basket's own refusals, one per row at most, each naming the row.
   *
   * ⚠️ **THE DUPLICATE CHECK IS NOT COSMETIC.** The server holds a UNIQUE key
   * over the row and the product, so a repeated product is a 409 — a refusal
   * about a database constraint, arriving after the round trip, which a person
   * cannot act on because nothing on screen says which of their rows is the
   * repeat. Refused here instead, naming the product and the row that already
   * has it.
   *
   * ⚠️ **THE QUANTITY MUST BE A WHOLE NUMBER, AND THIS PARAGRAPH REPLACES ONE
   * THAT SAID THE OPPOSITE.** It used to read that the check "deliberately does
   * not require a whole number, even though a quote line does", because the
   * planned figure was a decimal string on this endpoint and refusing a
   * fraction would have been this app enforcing a rule the API did not have.
   *
   * **The API has the rule now** — the owner ruled on 2026-09-02 that a planned
   * quantity is a whole number, and the column became `INT UNSIGNED` to match
   * the line it prefills. So this is a MIRROR of the server, not a restriction
   * of our own: `2.50` is a 422 whichever way it is sent.
   *
   * ⚠️ **It is mirrored rather than left to the round trip because of WHERE the
   * server's refusal lands.** A per-entry 422 arrives at an indexed path
   * (`products.2.quantity`), which this form deliberately does not route to a
   * control — see `FIELD_KEYS` — so it renders in the form-level banner and the
   * row itself is not marked. Refused here instead, the message sits under the
   * box that holds the number.
   *
   * ⚠️ **The message says what is WANTED, not that the value is wrong.**
   * Somebody who typed `2.5` and reads "invalid" is left to guess that halves
   * are the objection; "a whole number above zero" is the whole answer, and it
   * covers the non-numeric case in the same sentence.
   *
   * An empty row is REFUSED rather than dropped. Silently discarding a row
   * somebody added is a decision taken on their behalf that leaves no trace,
   * and the correct answer — remove it — is one click either way.
   */
  function validateProducts(): Record<number, ProductDraftError> {
    const errors: Record<number, ProductDraftError> = {};
    const firstRowByProduct = new Map<string, number>();

    productDrafts.forEach((draft, index) => {
      if (draft.productId === '') {
        errors[draft.uid] = {
          field: 'productId',
          message: `Choose a product for row ${index + 1}, or remove the row.`,
        };
        return;
      }

      const firstIndex = firstRowByProduct.get(draft.productId);
      if (firstIndex !== undefined) {
        errors[draft.uid] = {
          field: 'productId',
          message: `${productLabel(draft, index)} is already on row ${
            firstIndex + 1
          }. Change the quantity there, or remove this row.`,
        };
        return;
      }
      firstRowByProduct.set(draft.productId, index);

      /*
       * DIGITS ONLY, which is the server's own pattern rather than a looser
       * one restated. It is what refuses `2.5`, and no sign is accepted at all,
       * so a negative fails the shape rather than the comparison; `0` passes
       * the shape and fails the comparison, exactly as it does on the server.
       *
       * ⚠️ NOT `Number.isInteger(Number(typed))`, which is the obvious repair
       * and is wrong in two directions: `Number('0x10')` is 16 and
       * `Number('1e3')` is 1000, both integers, so a plan would carry a figure
       * nobody typed. The server refuses both with the same digits-only shape.
       *
       * ⚠️ **A BLANK QUANTITY IS FINE AND FALLS THROUGH TO THE AMOUNT CHECK.**
       * *SUPERSEDED: an `if (typed === '') return;` above this block.* It read
       * correctly while the quantity was the row's last control, and it would
       * now skip the amount check on every row whose quantity was left blank —
       * which is the common case, since blank means one.
       */
      const typedQuantity = draft.quantity.trim();
      if (typedQuantity !== '' && (!/^\d+$/.test(typedQuantity) || Number(typedQuantity) <= 0)) {
        errors[draft.uid] = {
          field: 'quantity',
          message: `How many ${productLabel(
            draft,
            index,
          )}? Enter a whole number above zero, or leave it blank for 1.`,
        };
        return;
      }

      /*
       * The product's step: the planned quantity becomes the raised line's
       * quantity, which the server refuses as 422 `QUANTITY_NOT_MULTIPLE`
       * unless it is a multiple. A blank box is 1.
       */
      const stepError = storedPlanPairs.has(`${draft.productId}|${draft.quantity}`)
        ? null
        : quantityStepError(
            catalogue.units.get(draft.productId),
            typedQuantity === '' ? '1' : typedQuantity,
          );
      if (stepError !== null) {
        errors[draft.uid] = { field: 'quantity', message: stepError };
        return;
      }

      /*
       * ⚠️ **MIRRORED FROM THE SERVER, for the same reason the quantity is** —
       * a per-entry refusal arrives at `products.0.amount`, which this form
       * deliberately does not route to a control, so it would land in the banner
       * with the row itself unmarked. See {@link PLANNED_LINE_AMOUNT_PATTERN},
       * which is the server's own shape rather than a looser rule restated.
       *
       * ⚠️ **A blank box is not an error** — it is "nobody has priced this
       * line", the state `null` holds on the wire, and it is what OMITS the key.
       *
       * The message says what is WANTED rather than that the value is wrong,
       * and it names the minus sign: a credit note is planned as a negative, and
       * somebody who typed one and read "invalid" would conclude it is refused.
       */
      const typedAmount = draft.amount.trim();
      if (typedAmount !== '' && !PLANNED_LINE_AMOUNT_PATTERN.test(typedAmount)) {
        errors[draft.uid] = {
          field: 'amount',
          message: `How much will ${productLabel(
            draft,
            index,
          )} bill? Enter an amount like 9375.00 — at most two decimal places, and a minus sign for a credit.`,
        };
      }
    });

    return errors;
  }

  /**
   * The basket as the wire wants it, or an empty list when nobody planned one.
   *
   * ⚠️ **A blank quantity OMITS the key rather than sending `'1'`.** The server
   * defaults it, `''` is a 422 on a strict schema, and writing `'1'` here would
   * be this form deciding a default the API already owns — which then has to be
   * kept in step with it by somebody remembering.
   *
   * ⚠️ **A blank AMOUNT omits the key too, and `''` is a 422 there as well.**
   * Omitting it and sending `null` mean the same thing here and only here: the
   * basket is a WHOLE-SET REPLACE, so every entry describes its line completely
   * and the row it replaces is gone either way. That is why the omission is
   * safe, and it is the one place on this contract where absent and `null` do
   * agree — do not carry the reading across to the row's own fields.
   */
  function plannedProducts(): InvoiceTrackerPlannedProductInput[] {
    return productDrafts
      .filter((draft) => draft.productId !== '')
      .map((draft) => {
        const typedQuantity = draft.quantity.trim();
        const typedAmount = draft.amount.trim();
        const typedDescription = draft.description.trim();
        return {
          productId: Number(draft.productId),
          ...(typedQuantity ? { quantity: typedQuantity } : {}),
          ...(typedAmount ? { amount: typedAmount } : {}),
          /*
           * ⚠️ **SENT AS `null` WHEN EMPTY, NOT OMITTED — and that is the
           * opposite of the two keys above it.** `products` is a WHOLE-SET
           * REPLACE, so omitting and clearing have the SAME effect on a line
           * being rewritten from nothing; the difference is what the request
           * SAYS. `quantity` omits because the server has a real default of 1
           * to fall back to, and letting it apply is the honest instruction.
           * There is no default description, so `null` states the fact
           * directly.
           *
           * The server would also accept `''` here — it trims and folds — but
           * this form never sends one. One habit across the codebase is worth
           * more than exploiting a leniency the server is free to withdraw,
           * and `''` is the value that must never reach the document column.
           */
          description: typedDescription === '' ? null : typedDescription,
        };
      });
  }

  /**
   * The refusals this form makes on its own.
   *
   * ⚠️ **Three of these are stricter than the server, and each is deliberate
   * rather than mirrored.** Refusing something the API would accept is normally
   * a client-side restriction nobody decided on — it breaks nothing, fails no
   * test, and gets reported as "that account is not set up properly". These
   * three are the exceptions, and each is here because the permissive answer is
   * silent:
   *
   *  - **`kind`** — the server defaults it, and a default is exactly what must
   *    not happen on a field nobody can correct afterwards. See the state above.
   *  - **`plannedRaiseFrom`** — the server accepts a row with no date, and such
   *    a row RAISES NO REMINDER. That is a legitimate tracker row and it is not
   *    what this dialog is for: a "future document" nothing ever prompts anybody
   *    about is the reassuring failure this screen exists to prevent. Somebody
   *    who genuinely wants a dateless row makes it on the tracker itself.
   *    ⚠️ **IT BINDS ON AN EDIT TOO, WHICH IS A DECISION AND NOT AN
   *    OVERSIGHT.** A row typed on the tracker may legitimately carry no date,
   *    and opening it here then refuses to save until one is chosen — visibly,
   *    with the reason on the field. The alternative is worse: this dialog would
   *    quietly become the place a reminder gets removed, and a plan nothing
   *    prompts anybody about is exactly what it exists to stop.
   *  - 🔴 **AN AMOUNT WITH NO CURRENCY — added 2026-09-02, and it is the
   *    NEWEST of the three, so weigh it as a judgement rather than a
   *    precedent.** The server takes the two keys independently and is right to:
   *    each is optional on its own, and a plan with neither is ordinary. The
   *    PAIR is the problem. A bare `9375.00` on a finance forecast is a figure
   *    with no unit, sitting in a list beside figures in other currencies, and
   *    the way it fails is by being totalled or read as the reader's own
   *    currency — no error, nothing on screen wrong, a number that is simply
   *    not what it appears to be. It is also nearly free to satisfy now that
   *    the currency derives itself: the refusal can only fire where a company
   *    and its billing region BOTH state nothing, and one click clears it.
   *    Neither key is made required by this — only the combination is refused.
   *
   * Everything else is left to the server. In particular the amount check
   * refuses only what is not a number at all — a negative is the server's call,
   * since a credit is a real thing, and so is the decimal-places limit.
   */
  function validate(): Partial<Record<FieldKey, string>> {
    const errors: Partial<Record<FieldKey, string>> = {};

    if (kind === '') {
      errors.kind = 'Choose whether a quote or an invoice is being planned.';
    }

    // The server refuses a row carrying neither, as a 422 on `partyName`.
    // ⚠️ Skipped where the party is the DOCUMENT'S: on an attached row those two
    // controls are not rendered, so a refusal about them would mark nothing.
    if (standaloneEditable && mode === 'company' && companyId === '') {
      errors.companyId = 'Choose the company this is for, or type a name instead.';
    }
    if (standaloneEditable && mode === 'name' && partyName.trim() === '') {
      errors.partyName = 'Type who this is for, or choose a company instead.';
    }

    /*
     * 🔴 **REQUIRED ON A CREATE; ON AN EDIT, REQUIRED ONLY ONCE SOMEBODY HAS
     * CHOSEN ONE. THAT ASYMMETRY IS THE WHOLE RULE AND IS NOT AN OVERSIGHT.**
     *
     * 17 rows imported before this column existed name no project, and the owner
     * intends to fill them in by hand through this dialog. Requiring it flatly
     * would make every one of those rows UNEDITABLE — somebody correcting a typo
     * in the Ref notes would be made to assign a project they may not know yet,
     * on a form whose Save is otherwise perfectly valid. So the refusal fires
     * where the plan is being MADE and stands down where it is merely being
     * touched. It is the same shape the server is taking: refuse on `POST`,
     * stay optional on `PATCH`.
     *
     * ⚠️ **`projectTouched` rather than `projectId !== seed`**, because the two
     * answer different questions: a person who opened the picker and chose the
     * project the row already held has still engaged with the field, and one who
     * never opened it has not. Today the picker offers no way to UNSET a value,
     * so the touched branch cannot actually fire — it is written out anyway so
     * that adding an `emptyOptionLabel` later cannot silently let a plan be
     * cleared to nothing.
     *
     * ⚠️ **Gated on `standaloneEditable` like the party rule above**: an attached
     * row renders no picker, and `projectId` is a 422 there rather than a column,
     * so a refusal about it would mark a control that does not exist.
     */
    if (standaloneEditable && projectId === '' && (editing === null || projectTouched)) {
      errors.projectId = 'Choose the project this is for.';
    }

    if (plannedRaiseFrom === '') {
      errors.plannedRaiseFrom = 'Choose the date this should be created on.';
    } else if (!isRealDate(plannedRaiseFrom)) {
      // Mirrors the API's own calendar check: 2026-02-30 satisfies a regex, and
      // `new Date` rolls it forward to 2 March rather than rejecting it.
      errors.plannedRaiseFrom = 'Enter a real date.';
    }

    /*
     * ⚠️ Not the server's exact money pattern, which also caps decimals at two.
     * A stricter client rule would refuse figures the API takes; this refuses
     * only what cannot be a number, and the server names the field for the rest.
     */
    const typedAmount = amount.trim();
    /*
     * ⚠️ **THE PAIR RULE FOLLOWS THE FIGURE THAT IS ACTUALLY STATED, WHICH IS
     * SOMETIMES THE DERIVED ONE.** A row whose lines are priced shows the SUM
     * and no longer reads the typed box at all — so checking the typed box there
     * would let a plan carrying a real total go out with no currency, which is
     * the exact "a figure with no unit on a finance forecast" this refusal
     * exists for. The format half is skipped instead, because a derived total is
     * well-formed by construction: `sumDecimalStrings` produced it.
     */
    if (standaloneEditable && !amountIsDerived && typedAmount !== '' && !/^-?\d+(\.\d+)?$/.test(typedAmount)) {
      errors.amount = 'Enter the amount as a number, like 9375.00.';
    } else if (standaloneEditable && shownAmount.trim() !== '' && effectiveCurrency === '') {
      // The pair rule — see the third bullet above.
      errors.currency = amountIsDerived
        ? 'Choose the currency the product amounts are in.'
        : 'Choose the currency this amount is in.';
    }

    return errors;
  }

  /**
   * ⚠️ **Only the keys somebody filled in.** The schema is `.strict()` and an
   * EMPTY STRING is a 422 rather than "no value", so an untouched box is omitted
   * entirely — never sent as `''`, and never as `null`, which on a create would
   * assert an absence rather than decline to say.
   *
   * ⚠️ **`kind` IS ALWAYS SENT**, and never conditionally: `validate()` has
   * already refused the empty case, and the whole point of requiring the choice
   * is that the server's `'invoice'` default is never what decides it.
   *
   * ⚠️ **`currency` IS THE EFFECTIVE ONE, derived or chosen.** The plan is what
   * PREFILLS the real document — `AccountsDashboardPage.prefillOf` maps
   * `row.currency` onto the quote and invoice prefills, and a prefilled currency
   * OUTRANKS both forms' own company derivation (`currencyTouched` is seeded
   * true from the prefill in `QuoteFormModal`, `currencyPrefilled` does the same
   * job in `InvoiceFormModal`). So this value is not a preview of what the
   * document will show — it is what the document WILL show, which is the whole
   * reason it has to be derived correctly here rather than left blank.
   *
   * ⚠️ **`amount` stays a STRING the whole way.** A float round trip is how
   * trailing zeros and precision go missing between the box and the column.
   *
   * ⚠️ **`comment` carries the "Ref notes" box.** One value, two labels — see
   * the state declaration.
   *
   * The return type is the API's own input interface, so a key this form invents
   * is a compile error rather than a 422 on a strict schema.
   */
  function buildBody(kindChosen: InvoiceTrackerKind): InvoiceTrackerStandaloneInput {
    const trimmedName = partyName.trim();
    const trimmedAmount = amount.trim();
    const trimmedNotes = refNotes.trim();
    const basket = plannedProducts();
    return {
      kind: kindChosen,
      // Exactly one of the two, decided by the mode rather than by which box
      // happens to hold something.
      ...(mode === 'company' ? { companyId: Number(companyId) } : { partyName: trimmedName }),
      /*
       * ⚠️ **ALWAYS SENT, AND NEVER CONDITIONALLY — the same treatment `kind`
       * gets above and for the same reason.** `validate()` has already refused
       * the empty case on a create, so this is a real id; a conditional spread
       * here would quietly let a plan through with no project on whatever
       * combination of state slipped past the check, which is exactly the
       * silent sales-pursuit no-op the column exists to prevent. The server does
       * not require the key YET (owner decision 2026-09-17: it shortly will), so
       * this form is ahead of the wire in the harmless direction — the API takes
       * the value either way.
       */
      projectId: Number(projectId),
      ...(effectiveCurrency ? { currency: effectiveCurrency } : {}),
      /*
       * ⚠️ **NOT SENT AT ALL WHERE THE LINES ANSWER IT.** The server derives the
       * row's amount from the priced lines (migration 147) and reads this column
       * only when none of them carries one, so writing the sum here would make
       * this form a second writer of a figure the API computes — and the copy
       * would be stale the moment a line amount moved, with the read still
       * correct and nothing to say the column disagreed. The typed value is
       * KEPT in state rather than cleared, so removing the line amounts brings
       * back what the person typed instead of losing it.
       */
      ...(plannedTotal === null && trimmedAmount ? { amount: trimmedAmount } : {}),
      /*
       * ⚠️ **OMITTED WHEN EMPTY, NEVER SENT AS `[]`.** `products` is a
       * whole-set replace: `[]` is the explicit "clear the basket" and an
       * absent key is "leave it alone". They agree on a create and stop
       * agreeing the moment this shape is used for an edit, which is a live
       * possibility — `api.updateInvoiceTrackerRow` takes this same type. The
       * conditional spread is what makes an untouched editor unable to wipe a
       * basket somebody planned.
       */
      ...(basket.length > 0 ? { products: basket } : {}),
      // The date the reminder task is due on, at 09:00 in the assignee's own
      // timezone. `plannedRaiseTo` is deliberately not offered here: one row,
      // one reminder, and a second date would be a window nobody asked for on a
      // form whose whole subject is a single day.
      plannedRaiseFrom,
      ...(trimmedNotes ? { comment: trimmedNotes } : {}),
    };
  }

  /**
   * ONLY WHAT CHANGED — the PATCH body for an edit.
   *
   * ⚠️ **`{}` IS A 422** ("At least one field is required"), which is why Save is
   * disabled while this comes back empty rather than the emptiness being
   * discovered by the server.
   *
   * ⚠️ **`null` CLEARS AND `''` IS A 422 ON THE MONEY AND DATE FIELDS**, so an
   * emptied box sends `null` and never the empty string. The two behave
   * differently in the same body — `partyName` and `comment` run through
   * `optionalText` and would take either — so every clear below is written as
   * `null` and none of them relies on the looser half.
   *
   * 🔴 **`kind` IS SENT WHEN IT CHANGED, ON A STANDALONE ROW — 2026-10-11.**
   * *SUPERSEDED: "`kind` IS NEVER HERE. It is absent from the server's update
   * schema."* The server accepts it now; on an attached row it must agree with
   * the document, so it is gated on `standaloneEditable` like the keys below.
   *
   * ⚠️ **Every STANDALONE-ONLY key is gated on `standaloneEditable`**, whose
   * controls are the ones not rendered for an attached row. That is the guard: a
   * key whose control does not exist cannot have changed, so it cannot be
   * emitted.
   */
  function buildPatch(target: InvoiceTrackerRow): InvoiceTrackerUpdateInput {
    const patch: InvoiceTrackerUpdateInput = {};

    // `validate()` has already refused the empty string, so this is a real date.
    if (plannedRaiseFrom !== (target.plannedRaiseFrom ?? '')) {
      patch.plannedRaiseFrom = plannedRaiseFrom;
    }

    const trimmedNotes = refNotes.trim();
    if (trimmedNotes !== (target.comment ?? '')) {
      patch.comment = trimmedNotes === '' ? null : trimmedNotes;
    }

    if (!standaloneEditable) return patch;

    // Only when it moved. `kind === ''` is unreachable on an edit (it is seeded
    // from the row and the empty option is not offered), and re-tested so the
    // narrowing is real to the compiler.
    if (kind !== '' && kind !== target.kind) {
      patch.kind = kind;
    }

    /*
     * ⚠️ **THE PARTY IS SENT AS A PAIR, AND ONLY THE PAIR IS CORRECT.** Switching
     * from a company to a typed name has to CLEAR the company, and switching the
     * other way has to clear the name — sending only the half that was filled in
     * leaves the other standing, and the row then reads back the stale one
     * because a company-backed row derives `partyName` from its company. Exactly
     * one of the two ever describes the party, so both keys move together or
     * neither does.
     */
    const seedMode: PartyMode = target.company === null ? 'name' : 'company';
    const seedCompanyId = target.company === null ? '' : String(target.company.id);
    const seedPartyName = target.company === null ? target.partyName : '';
    const trimmedName = partyName.trim();
    if (mode !== seedMode || companyId !== seedCompanyId || trimmedName !== seedPartyName) {
      if (mode === 'company') {
        patch.companyId = Number(companyId);
        patch.partyName = null;
      } else {
        patch.companyId = null;
        patch.partyName = trimmedName;
      }
    }

    /*
     * ⚠️ **SENT ONLY WHEN IT MOVED, AND THAT IS THE GUARD RATHER THAN A
     * TIDINESS.** `projectId` carries the same three instructions every other
     * key on this contract does — omitting leaves the stored project alone,
     * `null` CLEARS it, a number sets it — so a PATCH built from this form's own
     * STATE rather than from a diff would send `null` for a picker nobody opened
     * and wipe a project somebody chose, with a `200` and nothing to notice. The
     * diff below is what makes that unreachable.
     *
     * ⚠️ **It sits AFTER the `standaloneEditable` return above**: the key is a
     * **422** on an attached row, whose project lives on the real document's
     * lines and has no column here to write.
     *
     * The `''` → `null` arm cannot fire today — the picker offers no way to
     * unset — and is written out rather than dropped so that the clearing
     * instruction is stated where somebody adding an `emptyOptionLabel` will
     * read it, instead of `undefined` being sent and silently leaving the old
     * project standing.
     */
    const seedProjectId = target.project === null ? '' : String(target.project.id);
    if (projectId !== seedProjectId) {
      patch.projectId = projectId === '' ? null : Number(projectId);
    }

    if (effectiveCurrency !== (target.currency ?? '')) {
      patch.currency = effectiveCurrency === '' ? null : effectiveCurrency;
    }

    /*
     * ⚠️ **THE TYPED AMOUNT IS SENT ONLY WHERE IT IS THE ANSWER.** While the
     * lines are priced the server derives the row's total from them and this
     * column is not read at all, so writing it would be a second writer of a
     * figure the API owns — and the box on screen is not even showing it.
     *
     * ⚠️ **`seed === null` MEANS THE COLUMN COULD NOT BE READ, NOT THAT IT WAS
     * EMPTY** — the row arrived with its total derived, so `row.amount` was the
     * SUM rather than the stored value. Somebody who has since cleared the line
     * amounts is looking at an empty, editable box, and the row must end up
     * agreeing with what they were shown: the key is sent, clearing whatever the
     * unread column happened to hold.
     */
    if (plannedTotal === null) {
      const trimmedAmount = amount.trim();
      const seed = derivedAtOpen ? null : target.amount;
      if (seed === null || trimmedAmount !== seed) {
        patch.amount = trimmedAmount === '' ? null : trimmedAmount;
      }
    }

    /*
     * ⚠️ **THE ONE KEY THAT DESTROYS DATA WHEN IT IS SENT BY ACCIDENT.**
     * `products` is a WHOLE-SET REPLACE, so `[]` CLEARS a plan somebody typed
     * and answers 200 with nothing to notice. It is sent only where somebody
     * actually edited the basket — see `basketTouched`, which only
     * {@link editBasket} can set, and which the attached-row branch above has
     * already returned before ever reaching.
     */
    if (basketTouched) patch.products = plannedProducts();

    return patch;
  }

  /**
   * The PATCH this form would send, resolved during render.
   *
   * ⚠️ **ONE ANSWER, READ BY BOTH THE BUTTON AND THE REQUEST.** Deriving "has
   * anything changed" separately from the body that is sent is how a Save button
   * comes to be enabled over an empty patch, or disabled over a real one.
   */
  const pendingPatch = editing === null ? null : buildPatch(editing);

  /**
   * The PATCH, once it is known to be one this form can actually send — id, body
   * and all.
   *
   * ⚠️ **IT EXISTS SO THE SEND BELOW HAS NO `else` THAT FALLS BACK TO CREATE.**
   * Written as a chain of `||`s at the call site, an edit that failed any one of
   * those tests would have POSTED a new standalone row: a second plan for the
   * same billing, a `201` that reads as success, and nothing anywhere
   * reconciling the two. A null-or-a-whole-target makes create reachable only
   * from `editing === null`.
   */
  const patchTarget =
    editing !== null &&
    editing.id !== null &&
    pendingPatch !== null &&
    Object.keys(pendingPatch).length > 0
      ? { id: editing.id, body: pendingPatch }
      : null;

  /**
   * Why Save cannot be pressed, or `null` when it can.
   *
   * ⚠️ **A SENTENCE RATHER THAN A BARE `disabled`.** An unexplained disabled Save
   * over a form somebody has been typing in reads as a broken button; this names
   * the state, which is the documented exception to the no-helper-text rule.
   */
  const saveBlockedReason =
    editing === null
      ? null
      : editing.id === null
        ? // A row with no overlay id has nothing to put in the PATCH path — that
          // is the upsert, addressed at the collection, and this dialog does not
          // do it. Unreachable on a standalone row, which IS an overlay row.
          'This planned row has no saved id, so it can only be edited on the invoice tracker.'
        : pendingPatch !== null && Object.keys(pendingPatch).length === 0
          ? 'Nothing has changed yet.'
          : null;

  function save() {
    if (saving) return;

    const errors = validate();
    const basketErrors = validateProducts();
    if (Object.keys(errors).length > 0 || Object.keys(basketErrors).length > 0) {
      setFieldErrors(errors);
      setProductErrors(basketErrors);
      setFormMessages([]);
      return;
    }
    // Narrowed by `validate`, which refuses the empty string above. Re-tested
    // rather than asserted so the narrowing is real to the compiler.
    if (kind === '') return;
    /*
     * ⚠️ **AN EDIT WITH NOTHING TO SEND STOPS HERE RATHER THAN FALLING THROUGH
     * TO A CREATE.** `saveBlockedReason` already disables the button on both of
     * the states that produce it, and a disabled button is the optimisation
     * where this is the guarantee.
     */
    if (editing !== null && patchTarget === null) return;

    setSaving(true);
    setFieldErrors({});
    setProductErrors({});
    setFormMessages([]);

    /*
     * ⚠️ **TWO VERBS, ONE HANDLER.** Everything after this — the 401 hand-off,
     * the 403, the field routing, the banner — is identical for both, and a
     * second copy of it is how the create path and the edit path come to word
     * one refusal two ways.
     */
    (patchTarget === null
      ? api.createInvoiceTrackerRow(buildBody(kind))
      : api.updateInvoiceTrackerRow(patchTarget.id, patchTarget.body)
    )
      .then(() => {
        setSaving(false);
        onCreated();
        onClose();
      })
      .catch((caught: unknown) => {
        setSaving(false);

        /*
         * 401 is left entirely to `api.ts`, which attempts one refresh and then
         * ends the session. A message here would contradict it midway through
         * signing out.
         */
        if (caught instanceof ApiError && caught.status === 401) return;

        if (caught instanceof ApiError) {
          /*
           * A 403 carries no `details` at all, so it can never be a field error.
           * It can arrive even though the button was enabled, because a role can
           * change under a live access token — a gate on the page is the
           * optimisation, this branch is the guarantee.
           */
          if (caught.status === 403) {
            setFormMessages([
              `${
                futureBillablesDenialReason(ability) ??
                'Your account no longer has permission to plan documents.'
              } Nothing was saved.`,
            ]);
            return;
          }

          const { fields, unroutable } = caught.routeFieldErrors([...FIELD_KEYS]);
          const marked: Partial<Record<FieldKey, string>> = {};
          for (const key of FIELD_KEYS) {
            const message = fields[key];
            if (message) marked[key] = message;
          }
          setFieldErrors(marked);

          const banner = describeSaveFailure(caught, unroutable);
          /*
           * Never leave the user with nothing. If everything routed to an input
           * the banner is empty and the inputs carry the whole story; if nothing
           * routed, the sentence above is what explains the refusal.
           */
          setFormMessages(Object.keys(marked).length > 0 && unroutable.length === 0 ? [] : banner);
          return;
        }

        setFormMessages([
          caught instanceof Error
            ? `Nothing was saved. ${caught.message}`
            : 'Could not reach the server. Nothing was saved.',
        ]);
      });
  }

  const companyFootnote =
    companies.total > companies.options.length
      ? `Showing ${companies.options.length} of ${companies.total}. Type to search all.`
      : undefined;

  const currencyNote = currencyNoteFor(derivedCurrency, mode, currencyTouched);

  /**
   * What a failed currency-master read means HERE, which depends on whether
   * anything derived.
   *
   * ⚠️ **`currencyOptions` pins a code back into the list only when the list is
   * NON-EMPTY** — read it in `@/lib/use-invoice-options`, which is shared with
   * the quote and invoice forms and is not this change's to alter. So on a
   * failed read the picker renders BLANK even though `effectiveCurrency` holds
   * a derived code, and that code is still what `buildBody` sends. A bare
   * "couldn't load the currencies" beside an empty-looking box would read as
   * the plan having no currency, which is the opposite of what would be saved,
   * so the message names the code instead.
   */
  const currencyListError = currencies.failed
    ? effectiveCurrency === ''
      ? 'Couldn’t load the currency list, so one can’t be chosen here. Everything else on this form still saves.'
      : `Couldn’t load the currency list, so this can’t be changed here. The plan will be saved in ${effectiveCurrency}.`
    : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={editing === null ? 'Plan a future quote or invoice' : 'Edit a planned quote or invoice'}
      /*
       * ⚠️ **NO `description`, AND IT IS NOT COMING BACK.** Owner's
       * instruction, 2026-09-03: strip the helper text from this dialog. The
       * house rule names a `Modal`'s `description` as helper text by
       * definition, so this is the rule being applied rather than a taste call.
       *
       * ⚠️ **SUPERSEDED, and the argument is recorded because it is the one a
       * reader will re-derive**: *"ON SCREEN, IN FULL, AND NOT IN A TOOLTIP —
       * what this button does and does NOT do is the whole distinction the
       * screen turns on"*. It planned and reminded, it did not raise, and the
       * paragraph said so. What went with it: that sentence, and the hints on
       * the DATE and on **Ref notes**.
       *
       * ⚠️ **DO NOT REINSTATE IT AS A TOOLTIP, A `hint`, OR A PARAGRAPH IN THE
       * BODY.** The same fact re-entering through a different slot is the same
       * removal undone; the title says which act this is, and the section
       * labels say what each answer is for.
       */
      /*
       * ⚠️ **`xl` IS EARNED BY THE LINE ITEMS, NOT BY THE FIELDS — and the
       * two-column body below is the other half of the same change.**
       * *SUPERSEDED: `size="lg"` (`sm:max-w-3xl`), 2026-09-11, owner's request
       * to widen this dialog.* The house rule is "reach for `xl` when the
       * content is a TABLE, not to widen a column of fields", and this dialog
       * qualifies on the first half: **What is being sold** is a row of
       * product · quantity · amount · Remove, and it was the cramped part —
       * a product named `Sales - STEM Masters Festival - Participation Fee`
       * filled its picker at `lg`.
       *
       * ⚠️ **`xl` ALONE WOULD HAVE MADE THIS WORSE.** The body was a
       * single-column `grid`, so every `Input` would have stretched to ~1230px
       * — which is the exact misuse the rule names. It is `lg:grid-cols-2`
       * now, so the short fields pair up and only the line items and the notes
       * span the full width. **Do not revert one without the other**: `xl` with
       * a one-column body is a worse dialog than `lg` was.
       *
       * Below `sm` the panel is `w-full` and the grid is one column, so the
       * 320px case is untouched by both halves.
       */
      size="xl"
      // There is typed text in here that a clipped backdrop must not discard.
      dismissOnBackdrop={false}
      footer={
        <>
          {/*
            ⚠️ Why Save cannot be pressed, beside the button it is about — see
            `saveBlockedReason`. `sm:mr-auto` puts it at the far end of the row
            from the actions; below `sm` the footer is `flex-col-reverse`, so it
            settles under them rather than between them.
          */}
          {saveBlockedReason !== null && (
            <p className="m-0 self-center text-xs text-fg-subtle sm:mr-auto">
              {saveBlockedReason}
            </p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || saveBlockedReason !== null}
            loading={saving}
          >
            {editing === null ? 'Save plan' : 'Save changes'}
          </Button>
        </>
      }
    >
      {/*
        * ⚠️ **TWO COLUMNS FROM `lg`, AND EVERY CHILD DECIDES ITS OWN SPAN.**
        * Sections that are a single short answer take one column; the ones that
        * are themselves a row of controls — the line items — or that need the
        * measure — Ref notes — carry `lg:col-span-2`. See the note on the
        * `Modal`'s `size`, which is the other half of this.
        *
        * ⚠️ **SEVERAL CHILDREN ARE CONDITIONAL (`standaloneEditable`), so the
        * pairing is NOT fixed.** On an ATTACHED row most of this body is absent
        * and what remains simply reflows — which is why the spans are declared
        * per child rather than by writing a fixed two-up arrangement here. Do
        * not "tidy" this into explicit rows.
        */}
      <div className="grid gap-5 lg:grid-cols-2">
        {formMessages.length > 0 && (
          <Notice tone="danger" role="alert" className="lg:col-span-2">
            {formMessages.map((message) => (
              <p key={message} className="m-0 min-w-0 [overflow-wrap:anywhere]">
                {message}
              </p>
            ))}
          </Notice>
        )}

        {/*
          * 🔴 **A DROPDOWN, LIKE EVERY OTHER VALUE ON THIS FORM — owner's
          * instruction, 2026-09-03.**
          *
          * ⚠️ *SUPERSEDED: "**TWO BUTTONS RATHER THAN A `SegmentedControl`, AND
          * THE REASON IS THE UNSELECTED STATE.** `SegmentedControl` takes a
          * `value` of the option union, so 'nobody has chosen yet' is not a value
          * it can hold — expressing it would mean widening the union with an
          * empty member that renders as a third segment, or passing a value that
          * matches no option and relying on that."*
          *
          * **That objection was real and a native `<select>` simply does not
          * have it**: an unchosen state is an ordinary `<option value="">`, which
          * is how `kind` was already modelled in state (`InvoiceTrackerKind | ''`)
          * and is what `validate()` already refuses. So the empty option is the
          * union's empty member, rendered — nothing was widened and nothing is
          * being relied on.
          *
          * ⚠️ **THE PARTY TOGGLE BELOW IS DELIBERATELY NOT CHANGED WITH IT, and
          * the old comment's "so one screen reads one way" is what has to be
          * given up.** They are different kinds of control: this one records a
          * VALUE that goes on the wire, where that one is a MODE deciding which
          * of two controls renders underneath it. Putting a mode switch in a
          * dropdown hides the fact that choosing changes what the form asks for
          * next.
          */}
        {/*
          * 🔴 **EDITABLE ON A STANDALONE ROW SINCE 2026-10-11** (owner: "The kind
          * of future billables should be editable"; `kind` joined the server's
          * `updateSchema`). *SUPERSEDED: "ON AN EDIT IT IS A FACT, NOT A CONTROL
          * … `kind` is absent from the server's update schema, so there is
          * nothing to offer."*
          *
          * ⚠️ **ON AN ATTACHED ROW IT IS STILL A FACT, rendered as plain TEXT.**
          * The row's kind is read from the real quote or invoice, and the server
          * refuses any kind that disagrees with it — so there is nothing to
          * offer, and a disabled dropdown would read as a permission denial.
          */}
        {editing !== null && !standaloneEditable ? (
          <div className="grid min-w-0 gap-1.5">
            <p className={GROUP_LABEL}>Quote or invoice</p>
            <p className="m-0 text-sm text-fg">
              {/*
                * ⚠️ **EXHAUSTIVE, NOT A TWO-ARM TERNARY.** *SUPERSEDED:
                * `editing.kind === 'quote' ? 'A quote' : 'An invoice'`.* That
                * labelled a "Not known yet" row as **An invoice** the day
                * migration 161 landed — confidently, on the one row whose
                * whole meaning is that nobody has decided, and on a field this
                * very panel says cannot be corrected. `tsc` could not see it:
                * a ternary is not an exhaustive check, so the union gaining a
                * member changed nothing at compile time.
                */}
              {KIND_LABELS[editing.kind]}
            </p>
          </div>
        ) : (
        <div className="grid min-w-0 gap-1.5">
          <Field
            label="Quote or invoice"
            required
            error={fieldErrors.kind}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                value={kind}
                disabled={saving}
                invalid={fieldErrors.kind !== undefined}
                aria-describedby={describedBy}
                onChange={(event) => {
                  /*
                   * The cast is over the option values written directly below —
                   * a native select can only ever hand back one of its own
                   * `value`s, so this is narrowing a `string` the DOM has already
                   * constrained, not trusting one.
                   */
                  setKind(event.target.value as InvoiceTrackerKind | '');
                  clearError('kind');
                }}
              >
                {/*
                  * ⚠️ **THE EMPTY OPTION IS LOAD-BEARING ON A CREATE AND MUST NOT
                  * BE DROPPED THERE.** The server DEFAULTS an omitted `kind` to
                  * `'invoice'`, so a select that opened already reading "A quote"
                  * would file planned quotes as planned invoices for anybody who
                  * did not notice the field. See the note on the `kind` state,
                  * which is why `''` is a member of that union at all.
                  *
                  * ⚠️ **ON AN EDIT IT IS NOT OFFERED**: a saved row always holds
                  * a kind, so "not answered" is not a state it can return to.
                  */}
                {editing === null && <option value="">Choose…</option>}
                <option value="quote">A quote</option>
                <option value="invoice">An invoice</option>
                {/*
                  * ✅ **"Not known yet" (migration 161) — A REAL ANSWER, AND
                  * NOT A SECOND SPELLING OF THE EMPTY ROW ABOVE.** The empty
                  * row means "you have not answered"; this means "the answer
                  * is that nobody has decided", which is an ordinary state
                  * when somebody plans months out. They must stay two
                  * options: collapsing them would make the required-field
                  * refusal unreachable and let a mis-scroll save a plan.
                  *
                  * ⚠️ *SUPERSEDED 2026-10-11: "`kind` cannot be PATCHed, so a
                  * row saved as 'Not known yet' cannot later be edited to 'A
                  * quote'."* It can now, from this same control on an edit.
                  * RAISING still settles it to the kind of whatever document
                  * was actually created.
                  */}
                <option value="unknown">Not known yet</option>
              </Select>
            )}
          </Field>
          {/*
            * ✅ **THE "This can't be changed later" LINE IS REMOVED — 2026-10-11.**
            * It survived the no-helper-text rule only because it named an
            * IRREVERSIBLE consequence: `kind` was absent from the server's update
            * schema. `kind` is patchable now, so the sentence became false and
            * the exception that kept it no longer applies. Do not restore it.
            */}
        </div>
        )}

        {/*
          * ⚠️ **STANDALONE-ONLY, AND HIDING IT IS THE GUARD.** On a row attached
          * to a real quote or invoice the party is the DOCUMENT'S, read through
          * it at query time, and the server refuses `companyId` / `partyName`
          * against its `STANDALONE_ONLY_KEYS`. A control that does not render
          * cannot be changed, and `buildPatch` only ever emits a key that
          * changed — so the refusal is unreachable rather than handled.
          */}
        {standaloneEditable && (
        <div className="grid min-w-0 gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className={`${GROUP_LABEL} mr-auto`}>
              Who it is for
              <RequiredMark />
            </p>
            <Button
              type="button"
              size="sm"
              variant={mode === 'company' ? 'secondary' : 'ghost'}
              aria-pressed={mode === 'company'}
              disabled={saving}
              onClick={() => {
                setMode('company');
                clearError('partyName');
                /*
                 * The typed name is DISCARDED rather than carried across.
                 * Leaving it would show a name beside an unselected picker,
                 * which reads as a choice that failed to take — and only one of
                 * the two ever reaches the wire.
                 */
                setPartyName('');
              }}
            >
              An existing company
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === 'name' ? 'secondary' : 'ghost'}
              aria-pressed={mode === 'name'}
              disabled={saving}
              onClick={() => {
                setMode('name');
                clearError('companyId');
                setCompanyId('');
              }}
            >
              A new company
            </Button>
          </div>

          {/*
            Both branches label their control from the "Who it is for" heading
            above rather than repeating it, so `required` is deliberately absent
            from each Field — see `RequiredMark`, which is where the asterisk
            went instead.
          */}
          {mode === 'company' ? (
            <Field
              label="Company"
              hideLabel
              error={
                fieldErrors.companyId ?? (companies.failed ? 'Couldn’t load the company list.' : null)
              }
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={companyId}
                  onChange={(next) => {
                    setCompanyId(next);
                    clearError('companyId');
                    /*
                     * ⚠️ The currency is NOT cleared here, and must not be.
                     * While untouched it follows the new company wholesale
                     * (including to empty) through `effectiveCurrency`, and
                     * once touched it is a choice this handler has no business
                     * overwriting. Writing anything to it here would break one
                     * of those two rules whichever value was written.
                     */
                  }}
                  options={companies.options}
                  placeholder="Choose a company…"
                  searchPlaceholder="Search companies…"
                  onSearch={companies.setTerm}
                  loading={companies.loading}
                  disabled={saving}
                  invalid={fieldErrors.companyId !== undefined}
                  footnote={companyFootnote}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          ) : (
            <Field label="Company name" hideLabel error={fieldErrors.partyName ?? null}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={partyName}
                  maxLength={PARTY_NAME_MAX}
                  autoComplete="off"
                  disabled={saving}
                  invalid={fieldErrors.partyName !== undefined}
                  aria-describedby={describedBy}
                  onChange={(event) => {
                    setPartyName(event.target.value);
                    clearError('partyName');
                  }}
                />
              )}
            </Field>
          )}
        </div>
        )}

        {/*
          * ⚠️ **STANDALONE-ONLY, AND HIDING IT IS THE GUARD — exactly as it is
          * for the party above.** An attached row's project lives on the real
          * quote's or invoice's LINES; there is no column here to write, and the
          * server refuses `projectId` against its `STANDALONE_ONLY_KEYS`. A
          * control that does not render cannot be changed, and `buildPatch` only
          * ever emits a key that changed — so the refusal is unreachable rather
          * than handled.
          *
          * ⚠️ **ITS OWN `Field` WITH ITS OWN `required`, NOT the `RequiredMark`
          * helper.** That helper exists only because the party picker hides its
          * label behind the "Who it is for" heading, so `Field`'s own marker
          * would render into an `sr-only` element and mark nothing on screen.
          * This control has a visible label of its own, so `required` puts the
          * asterisk exactly where `Field` always puts it — the same treatment
          * "When to create it" below takes.
          *
          * ⚠️ **NOT folded into the "Who it is for" group.** A project is not
          * who: that heading names a party and carries a two-button mode switch
          * deciding which of two controls renders under it, and a third
          * unrelated control inside it would read as part of that choice.
          *
          * A bare `Field` takes no `className`, so the grid child is this
          * wrapper — the same shape the date below uses.
          */}
        {standaloneEditable && (
        <div className="grid min-w-0 content-start gap-1.5">
          {/*
            * ⚠️ **ABOVE THE PICKER AND OUTSIDE THE `Field`** — the pills choose
            * which cycles the list OFFERS, so they are a property of the LIST
            * rather than a second control the "Project" label describes. Same
            * placement as every other mount of this control, and the `gap-1.5`
            * matches `Field`'s own label-to-control rhythm so the three read as
            * one block rather than as a stray control above a labelled one.
            *
            * ⛔ **NO SENTENCE EXPLAINING WHAT THEY MEAN, HERE OR ANYWHERE.** The
            * two labels are the whole explanation the control gets — that is
            * what the no-helper-text rule requires and what
            * `PROJECT_CYCLE_SCOPE_LABELS` exists to hold in one place. The only
            * prose these pills may produce is the footnote below, and that one
            * reports a FACT about the list rather than advice about the control.
            */}
          <SegmentedControl
            /*
             * Names the CHOICE — it is the group's accessible name and is not
             * rendered visibly. A dialog mounts exactly one of these and its own
             * title names the record, so it needs nothing more specific; the
             * picker's own visible "Project" label sits directly beneath it.
             */
            label="Project cycle"
            value={scope}
            options={PROJECT_CYCLE_SCOPE_OPTIONS}
            onChange={(next) => {
              setScope(next);
              /*
               * ⚠️ Set here and NOWHERE ELSE. It is what stops the opening seed
               * overruling a pill somebody chose before the two reads landed.
               */
              setScopeTouched(true);
            }}
            /*
             * ⚠️ **Locked exactly where the picker below is locked, and for the
             * same reason.** A failed read leaves nothing to switch between, and
             * a live pair of pills over two empty lists reads as the pills being
             * what emptied them — which sends somebody clicking between them
             * instead of to the sentence that says the list could not be loaded.
             */
            disabled={saving || projects.failed}
          />

          <Field
            label="Project"
            required
            error={
              fieldErrors.projectId ??
              /*
               * ⚠️ **A FAILED READ IS A REAL ERROR AND SAYS SO — it is not
               * helper text and a sweep must not take it.** The list is
               * discarded rather than rendered short (see `useActiveProjects`),
               * so what is on screen is an empty, disabled picker over a
               * required field: without this sentence that is indistinguishable
               * from a catalogue with no projects in it, and from a control the
               * reader lacks the right to use.
               *
               * ⚠️ **THE COST, STATED RATHER THAN HIDDEN: a create cannot be
               * saved while this is showing.** That is the right direction —
               * the owner's rule is that a plan names a project, and one filed
               * against the wrong project because the right one silently failed
               * to load is worse than one that could not be filed at all. The
               * sentence is what turns a dead form into a fixable one.
               */
              (projects.failed
                ? 'Couldn’t load the project list, so one can’t be chosen. Try again in a moment.'
                : null)
            }
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                value={projectId}
                onChange={(next) => {
                  setProjectId(next);
                  /*
                   * ⚠️ Set here and NOWHERE ELSE. It is what makes the
                   * requirement fire on a create and stand down on a legacy row
                   * nobody has touched — see `validate()`.
                   */
                  setProjectTouched(true);
                  clearError('projectId');
                }}
                options={projectPicker.options[scope]}
                placeholder="Choose a project…"
                searchPlaceholder="Search projects…"
                /*
                 * ⚠️ **Conditionally spread, never `footnote={undefined}`** —
                 * the prop is optional and this repo's forms pass it this way so
                 * an absent sentence is an absent key rather than a declared
                 * one. See `projectScopeNote` for why it is a fact rather than
                 * helper text, and for why it stays silent while the read is in
                 * flight or has failed.
                 */
                {...(projectScopeNote ? { footnote: projectScopeNote } : {})}
                /*
                 * ⚠️ **NO `onSearch`, AND THAT IS THE POINT OF PAGING THE LIST
                 * IN.** Every option is already here, so the box filters what is
                 * in hand rather than asking the server — which is what makes
                 * the tail of the alphabet reachable by typing. A server-side
                 * search over a 100-row page would have hidden the same ~20
                 * projects behind a control that looked like it was searching
                 * all of them.
                 *
                 * ⚠️ **Since the pills, it searches THE PILL and not the whole
                 * catalogue** — typing a closed-cycle project's name under
                 * `Current` finds nothing, and the footnote above does NOT cover
                 * that (it speaks only when a pill is empty outright). That is
                 * the trade the pills are, taken on the owner's instruction and
                 * the same one every other mount of this control makes; the
                 * repair is one click on `Previous`, which is why the labels
                 * carry the whole explanation.
                 */
                loading={projects.loading}
                /*
                 * ⚠️ **LOCKED ON A FAILED READ, never left as an empty list.**
                 * An empty picker asserts that there are no projects; a disabled
                 * one beside the sentence above says the question could not be
                 * asked. The `Field` error is what carries the reason, because
                 * a bare disabled control makes a failed read, a permission
                 * denial and an ordinary empty list identical.
                 */
                disabled={saving || projects.failed}
                invalid={fieldErrors.projectId !== undefined}
                aria-describedby={describedBy}
              />
            )}
          </Field>
        </div>
        )}

        {/*
         * ⚠️ **NO `hint`.** *SUPERSEDED: "The reminder is a task due at 9am on
         * this day, in your timezone, so it comes up at sign-in like any
         * other."* Removed with the rest of this dialog's helper text on the
         * owner's instruction, 2026-09-03.
         *
         * ⚠️ **That sentence was the LAST copy of the 9am/timezone fact on this
         * screen** — it had been moved here out of the modal's description
         * precisely so it sat on the control it was about, and the description
         * is gone too. It is not recorded anywhere else in the UI; the task the
         * plan creates is what carries it now.
         */}
        {/* A bare `Field` takes no `className`, so the grid child is this
            wrapper. One column: a date is the shortest answer on the form. */}
        <div className="grid min-w-0 content-start">
        <Field
          label="When to create it"
          required
          error={fieldErrors.plannedRaiseFrom ?? null}
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              type="date"
              value={plannedRaiseFrom}
              disabled={saving}
              invalid={fieldErrors.plannedRaiseFrom !== undefined}
              aria-describedby={describedBy}
              onChange={(event) => {
                setPlannedRaiseFrom(event.target.value);
                clearError('plannedRaiseFrom');
              }}
            />
          )}
        </Field>
        </div>

        {/*
         * ⚠️ **ONE CELL, NOT TWO FIELDS.** A figure and the currency it is in
         * are one fact to a reader — the same reading `InvoiceFormModal` takes
         * of the currency and the rate it is pinned at — and two separately
         * labelled boxes side by side invite somebody to fill one and not the
         * other. That combination is now the one thing `validate()` refuses
         * here, and the layout is what stops it being reached in the first
         * place.
         *
         * Amount on the left because it is the half being TYPED; the currency
         * sits at a fixed width on the right, where a unit belongs, and it is
         * usually already answered.
         *
         * Both halves are OPTIONAL and must stay so. A planned document usually
         * has an expected value and recording it is what makes the dashboard's
         * future section add up — but a plan made before the figure is settled
         * is an ordinary thing, and requiring one would push somebody to type a
         * number they do not have yet onto a finance screen.
         */}
        {standaloneEditable && (
        <div className="grid min-w-0 content-start gap-1.5">
          <p className={GROUP_LABEL}>Expected value</p>
          <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_9rem]">
            {/*
              ⚠️ A TEXT box with `inputMode="decimal"`, never `type="number"`. A
              number input hands back a coerced value and silently drops what it
              cannot parse, which is precisely the float round trip the decimal
              string exists to prevent — and its scroll-to-change behaviour edits
              money by accident.

              ⚠️ **READ-ONLY, NOT DISABLED, WHILE THE LINES ANSWER IT.** A
              disabled input drops out of the tab order and cannot be selected or
              copied, and it looks exactly like a control somebody lacks the
              right to use. `readOnly` keeps the figure reachable and says what it
              is: a value this form is not the writer of. The reason it cannot be
              typed in is stated below the row, because a bare read-only control
              makes a derived field, a permission denial and an ordinary value
              identical.
            */}
            <Field label="Expected amount" hideLabel error={fieldErrors.amount ?? null}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  value={shownAmount}
                  maxLength={20}
                  autoComplete="off"
                  disabled={saving}
                  readOnly={amountIsDerived}
                  // The surface ramp, never a hardcoded colour — it reads the
                  // same way in both themes, and matches what `disabled` does one
                  // step along so the two do not look like different states of
                  // different things.
                  className={amountIsDerived ? 'bg-surface-sunken text-fg-secondary' : undefined}
                  invalid={fieldErrors.amount !== undefined}
                  aria-describedby={describedBy}
                  onChange={(event) => {
                    setAmount(event.target.value);
                    clearError('amount');
                    // The pair rule can only have been about this combination,
                    // and the combination has just changed.
                    clearError('currency');
                  }}
                />
              )}
            </Field>

            <Field label="Currency" hideLabel error={fieldErrors.currency ?? currencyListError}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={effectiveCurrency}
                  onChange={(next) => {
                    /*
                     * ⚠️ **CHOOSING "No currency" COUNTS AS TOUCHING IT.** It is
                     * a deliberate act, and re-deriving over it would undo
                     * exactly the thing the person just did — the same reading
                     * every `touched` flag in this codebase takes of a cleared
                     * box.
                     */
                    setCurrencyTouched(true);
                    setChosenCurrency(next);
                    clearError('currency');
                  }}
                  options={currencyPickerOptions}
                  emptyOptionLabel="No currency"
                  placeholder="Choose a currency…"
                  searchPlaceholder="Search currencies…"
                  loading={currencies.loading}
                  disabled={saving}
                  invalid={fieldErrors.currency !== undefined}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          </div>
          {/*
            ⚠️ **WHY THE BOX CANNOT BE TYPED IN — and it is NOT helper text.**
            A read-only control with nothing beside it makes a derived figure, a
            permission denial and an ordinary value look identical, which is the
            documented exception. It names the way back as well as the state:
            clearing the product amounts is what hands the box over, and nothing
            else on screen says so.

            ⚠️ **Rendered ALONGSIDE any error rather than instead of one.** Both
            can be true at once — the total is derived AND has no currency — and
            a `??` between two true things is the silent drop this codebase
            records.
          */}
          {amountIsDerived && (
            <p className="m-0 text-xs text-fg-subtle">
              Added up from the product amounts below. Clear those to type a figure instead.
            </p>
          )}
          {/*
            The source line. Suppressed only while an error on either half is
            showing, so a refusal is never competing with a note about where a
            value came from.

            ⚠️ **And on an EDIT it is suppressed outright**, because nothing is
            being derived there: a stored currency is the row's own and the
            picker is an ordinary one. A note explaining a derivation that is
            switched off would be a sentence about machinery rather than about
            this row.
          */}
          {editing === null && fieldErrors.currency === undefined && currencyListError === null && (
            <p className="m-0 text-xs text-fg-subtle">{currencyNote}</p>
          )}
        </div>
        )}

        {/*
         * ⚠️ **THE PLANNED BASKET — SEVERAL PRODUCTS, EACH WITH A QUANTITY.**
         * The owner's decision, against one product per plan: a plan is the
         * whole basket, and raising the document prefills every line of it.
         *
         * ⚠️ **NOTHING HERE CREATES A LINE ITEM.** A quote or invoice line is
         * `POST /{quotes|invoices}/{id}/products` and cannot exist before its
         * document does. This is a note of what is intended; the lines are
         * created by the form that raises the document, seeded from
         * `plannedProductLines` in `@/lib/api`.
         *
         * The hairline above it is the only structural chrome on this form, and
         * it earns its place: everything below is optional, and a flat stack
         * makes six controls all look equally required.
         *
         * ⚠️ **STANDALONE-ONLY, AND THAT IS WHAT MAKES `products` SAFE TO SEND.**
         * An attached row's lines are the document's own and the key is refused
         * server-side; there is no value of `products` meaning "this row has none
         * of its own", so the editor is not rendered rather than sent empty.
         * Nothing can then set `basketTouched`, which is what puts the key on the
         * wire at all.
         */}
        {/* Full width: each row here is itself product · quantity · amount ·
            Remove, and it is the section `xl` was taken for. */}
        {standaloneEditable && (
        <div className="grid min-w-0 gap-3 border-t border-border pt-5 lg:col-span-2">
          <div className="flex flex-wrap items-start gap-2">
            {/*
              ⚠️ **THE LABEL ALONE.** *SUPERSEDED: "Optional. These carry into
              the document when you raise it; a blank quantity counts as one,
              and an amount is what the whole line is expected to bill."*
              Removed on the owner's instruction, 2026-09-03.

              ⚠️ **"Optional" is carried by the ABSENCE of a `RequiredMark`,
              which every required group on this form has** — so that word was
              the one part of the sentence already said by the layout. The
              blank-quantity default is carried by the quantity box's own
              `placeholder="1"`, which is a value rather than advice and stays
              for that reason.
            */}
            <div className="mr-auto min-w-0">
              <p className={GROUP_LABEL}>What is being sold</p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={saving}
              onClick={addProductRow}
            >
              Add a product
            </Button>
          </div>

          {/*
            The basket-level 422 — about the SET rather than a row. An indexed
            per-row refusal is not routed here; see `FIELD_KEYS`.
          */}
          {fieldErrors.products !== undefined && (
            <p role="alert" className="m-0 text-xs text-danger">
              {fieldErrors.products}
            </p>
          )}

          {/*
            ⚠️ Said even with rows already on screen: the list loads once per
            opening, so a failure mid-session leaves the picker empty for every
            row added afterwards, and an empty picker reads as an empty
            catalogue. Everything else on the form still saves.
          */}
          {catalogue.failed && (
            <Notice tone="danger">
              Couldn’t load the product list, so products can’t be planned here. Everything else on
              this form still saves.
            </Notice>
          )}

          {productDrafts.length > 0 && (
            <ul className="m-0 grid list-none gap-2 p-0">
              {productDrafts.map((draft, index) => {
                const rowError = productErrors[draft.uid];
                const label = productLabel(draft, index);
                return (
                  <li key={draft.uid} className="flex flex-wrap items-start gap-2">
                    <div className="min-w-0 flex-1 basis-48">
                      <Field
                        label={`Product for row ${index + 1}`}
                        hideLabel
                        error={rowError?.field === 'productId' ? rowError.message : null}
                      >
                        {({ id, 'aria-describedby': describedBy }) => (
                          <Combobox
                            id={id}
                            value={draft.productId}
                            onChange={(next) => setDraftProduct(draft.uid, next)}
                            options={optionsForDraft(draft)}
                            placeholder="Choose a product…"
                            searchPlaceholder="Search products…"
                            onSearch={catalogue.setTerm}
                            loading={catalogue.loading}
                            disabled={saving}
                            invalid={rowError?.field === 'productId'}
                            {...(catalogue.footnote ? { footnote: catalogue.footnote } : {})}
                            aria-describedby={describedBy}
                          />
                        )}
                      </Field>
                    </div>

                    {/*
                      ⚠️ A TEXT box, never `type="number"` — the same rule as
                      the amount above. The quantity crosses the wire as the
                      string that was typed.

                      ⚠️ `inputMode="numeric"` AND NOT `"decimal"`, which is
                      what it said while fractions were plannable. A planned
                      quantity is a whole number since 2026-09-02, and a decimal
                      keypad offers a point that is now a refusal — the keyboard
                      would be inviting the one thing the field cannot hold.
                      Still `maxLength` rather than a tighter cap: a limit that
                      silently stops accepting characters reads as a broken box,
                      where the check below says what is wanted.
                    */}
                    <div className="w-24">
                      <Field
                        label={`How many ${label}`}
                        hideLabel
                        error={rowError?.field === 'quantity' ? rowError.message : null}
                      >
                        {({ id, 'aria-describedby': describedBy }) => (
                          <Input
                            id={id}
                            inputMode="numeric"
                            value={draft.quantity}
                            maxLength={12}
                            autoComplete="off"
                            min={Number(defaultQuantityFor(catalogue.units.get(draft.productId)))}
                            step={Number(defaultQuantityFor(catalogue.units.get(draft.productId)))}
                            placeholder="1"
                            disabled={saving}
                            invalid={rowError?.field === 'quantity'}
                            aria-describedby={describedBy}
                            onChange={(event) => setDraftQuantity(draft.uid, event.target.value)}
                          />
                        )}
                      </Field>
                    </div>

                    {/*
                      ⚠️ **A FLAT AMOUNT FOR THE LINE. THE QUANTITY DOES NOT
                      MULTIPLY IT** — owner's decision, 2026-09-03, and the one
                      thing about this box a reader carries across wrongly, since
                      every neighbouring line table holds a UNIT price. Three at
                      `10000.00` plans ten thousand.

                      ⚠️ A TEXT box with `inputMode="decimal"`, never
                      `type="number"` — the same rule as the row amount above, and
                      for the same two reasons: a number input coerces, and its
                      scroll-to-change behaviour edits money by accident.

                      ⚠️ **EMPTY IS A REAL STATE AND IS NOT A ZERO.** It is
                      "nobody has priced this line", which OMITS the key; `0.00`
                      is a line that bills nothing. The box is seeded from the
                      catalogue on choosing a product and stops following the
                      moment anybody types in it — including to empty it. See
                      `ProductDraft.amountTouched`.
                    */}
                    <div className="w-32">
                      <Field
                        label={`How much will ${label} bill`}
                        hideLabel
                        error={rowError?.field === 'amount' ? rowError.message : null}
                      >
                        {({ id, 'aria-describedby': describedBy }) => (
                          <Input
                            id={id}
                            inputMode="decimal"
                            value={draft.amount}
                            maxLength={20}
                            autoComplete="off"
                            disabled={saving}
                            invalid={rowError?.field === 'amount'}
                            aria-describedby={describedBy}
                            onChange={(event) => setDraftAmount(draft.uid, event.target.value)}
                          />
                        )}
                      </Field>
                    </div>

                    {/*
                      ✅ **THE LINE DESCRIPTION (migration 160), AND IT IS THE
                      ONE CONTROL ON THIS ROW A CUSTOMER EVENTUALLY READS.**
                      Raising the plan copies it onto the document line's own
                      description, which prints on the quote or invoice.

                      ⚠️ **IT IS NOT "Ref notes", WHICH SITS TWO SECTIONS BELOW
                      AND IS PRIVATE.** That one is the row's `comment` — why a
                      document is expected, for whoever reads the tracker. These
                      two are the only free-text boxes on the form and they go
                      to opposite audiences, which is why this one is labelled
                      per line and placed inside the row rather than under it.

                      ⚠️ **IT TAKES THE REMAINING WIDTH** (`flex-1 min-w-0`)
                      rather than a fixed `w-`, because it is the only control
                      here whose useful length is unbounded — and it is why the
                      dialog went to `xl` with a two-column body in the same
                      week. Below `lg` the row wraps and this drops to its own
                      line, which is the right order: the product names the
                      thing, this describes it.

                      ⛔ **NO `hint` AND NO PLACEHOLDER PROSE.** The
                      no-helper-text rule binds here, and the one fact worth
                      warning about — that a description REPLACES the derived
                      `Product — Cities` on the document rather than adding to
                      it — cannot honestly be said at this control: the cities
                      are not known until the document exists. It is recorded on
                      `ProductDraft.description` instead.
                    */}
                    <div className="min-w-0 flex-1 basis-48">
                      <Field
                        label={`What ${label} covers`}
                        hideLabel
                        error={rowError?.field === 'description' ? rowError.message : null}
                      >
                        {({ id, 'aria-describedby': describedBy }) => (
                          <Input
                            id={id}
                            value={draft.description}
                            maxLength={500}
                            autoComplete="off"
                            placeholder="Description"
                            disabled={saving}
                            invalid={rowError?.field === 'description'}
                            aria-describedby={describedBy}
                            onChange={(event) =>
                              setDraftDescription(draft.uid, event.target.value)
                            }
                          />
                        )}
                      </Field>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={saving}
                      aria-label={`Remove ${label} from the plan`}
                      onClick={() => removeProductRow(draft.uid)}
                    >
                      Remove
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        )}

        {/*
         * ⚠️ "Ref notes" IS THE ROW'S `comment` FIELD — the owner's label over
         * the tracker's existing column, not a new one. See `buildBody` and the
         * state declaration. Do not add a second box beside it.
         *
         * ⚠️ **KEPT, and it is one of the four fields the owner named for this
         * screen.** Nothing else records why a document is expected, so removing
         * it would delete information no other field can carry.
         */}
        {/* Full width: free text wants the measure, and it closes the form. */}
        <div className="grid min-w-0 lg:col-span-2">
        <Field
          label="Ref notes"
          error={fieldErrors.comment ?? null}
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              value={refNotes}
              rows={3}
              maxLength={REF_NOTES_MAX}
              disabled={saving}
              invalid={fieldErrors.comment !== undefined}
              aria-describedby={describedBy}
              onChange={(event) => {
                setRefNotes(event.target.value);
                clearError('comment');
              }}
            />
          )}
        </Field>
        </div>
      </div>
    </Modal>
  );
}
